import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AOP_MAP_LOCATION_STATE,
  isAopMapRequested,
  type TSopViewMode,
} from '@/modules/aop-map/aop-view-state';
import { useBoolean, useDebounceCallback } from 'usehooks-ts';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import { BubbleMenu } from '@tiptap/react/menus';
import { CellSelection } from '@tiptap/pm/tables';

import {
  AccessDenied,
  AutomationPageHeader,
  type IAutomationPageHeaderProps,
  SupportAccessAlert,
  TooltipActionButton,
} from '@/shared/components';
import {
  Badge,
  Button,
  cn,
  CometBorder,
  DashedFrame,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  EmptyState,
  Flex,
  Icon,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  Spinner,
  Text,
  toast,
  ToolbarBadge,
  type IIconProps,
} from '@kognitos/lattice';

import { getListExceptionsQueryKey } from '@/generated/hooks/astral/v1/api';
import {
  V1AutomationActivationState,
  V1AutomationKind,
  V1AutomationStage,
  type Commonv1Value,
  type V1Automation,
  type V1AutomationConfig,
  type V1CodeManifest,
  type V1Step,
} from '@/generated/hooks/grimoire/models';
import {
  getListAutomationsInfiniteQueryKey,
  getListAutomationsQueryKey,
  getListAutomationSummariesInfiniteQueryKey,
  getListAutomationSummariesQueryKey,
  getListDefaultInputsHistoryQueryKey,
  getQueryAutomationQueryKey,
  useForkPublishedAutomation,
  useInvokeAutomation,
  useQueryAutomation,
  useSetAutomationDefaultInputs,
  useUpdateAutomation,
} from '@/generated/hooks/grimoire/v1/api';
import {
  getListRunsQueryKey,
  getQueryAutomationRunAggregatesQueryKey,
  getQueryDailyRunAggregatesQueryKey,
} from '@/generated/hooks/jeeves/v1/api';
import { getListThreadMessagesInfiniteQueryKey } from '@/generated/hooks/zen/v1/thread_service';
import {
  automationGetArtifactContent,
  automationUpdateArtifact,
  getAutomationGetEventsQueryKey,
  useAutomationCommitArtifacts,
  useAutomationDiscardArtifacts,
  useAutomationSendMessage,
} from '@/generated/hooks/rain/v1/automation_scoped/thread_service';

import { getRoutePath } from '@/routes/util';
import {
  selectAutomation,
  selectAutomationAOP,
  selectAutomationInputsForId,
  selectAutomationReset,
  selectAutomationSetError,
  selectAutomationSetLoading,
  selectClearNonDefaultInputs,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  selectGetAutomationInputs,
  selectHasDefaultInputsChanged,
  selectIDPPanelClose,
  selectIDPPanelDocumentName,
  selectIDPPanelExceptionFields,
  selectIDPPanelFields,
  selectIDPPanelIdpData,
  selectIDPPanelIsOpen,
  selectIDPPanelListItems,
  selectIDPPanelOnSave,
  selectIDPPanelReadOnly,
  selectIDPPanelSource,
  selectInitializeDefaultInputs,
  selectQuillChatClearAutomation,
  selectQuillChatGetThreadId,
  selectSetAutomationInputs,
  selectSyncOriginalDefaults,
  selectToggleDefaultInput,
  selectUpdateAutomation,
  useAutomationInputsStore,
  useAutomationStore,
  useIDPPanelStore,
  useQuillChatStore,
  useUserPreferencesStore,
} from '@/shared/stores';

import { AOPSkeleton } from '@/modules/automation';
import {
  useBlueprintBuildPhase,
  useResolvedAutomation,
  useRevisionContents,
} from '@/modules/automation/hooks';
import {
  VersionHistoryPanel,
  type IVersionHistoryItemData,
} from '@/modules/automation-details';
import { versionHistoryShowsTrackChanges } from '@/modules/automation-details/components/version-history/utils';

import { formatStepContext } from './manifest-step-chat-context';
import { ManifestSopView } from './ManifestSopView';
import { BlueprintDocumentView } from './BlueprintDocumentView';
import { DraftDocumentStepsView } from './DraftDocumentStepsView';
import {
  getBrowserPanelState,
  subscribeToBrowserPanel,
} from '@/modules/outputs/browser-panel-registry';
import {
  AutomationConfigRail,
  BrowserSessionsRail,
  DraftEditorRightRail,
  type IRailPrimaryAction,
} from './DraftEditorRightRail';
import { AutomationVersionSelect } from '@/modules/automation/components/AutomationVersionSelect';
import { AutomationTestPane } from './AutomationTestPane';
import {
  ASSET_VERSION_HISTORY_PARAM,
  AssetDefinitionLayout,
  AssetDocument,
  AssetVersionBadge,
  AssetWorkspaceLayout,
} from '@/layouts/AssetWorkspaceLayout';
import { AutomationWorkspaceHeader } from '@/modules/automation/components/AutomationWorkspaceHeader';
import {
  CHAT_DIVIDER_WIDTH_PX,
  forgetChosenChatWidth,
  getChatPanelSizes,
  rememberChosenChatWidth,
  shouldRememberChatWidth,
} from './chat-column-sizes';
import { ChatColumnDefaultWidth } from './ChatColumnDefaultWidth';
import { ExportAutomationDialog } from '../ExportAutomationDialog';
import { ManifestUpgradeBanner } from './ManifestUpgradeBanner';
import { TestRunBanner } from './TestRunBanner';
import { useTestRunManifest } from '@/modules/automation/hooks/useTestRunManifest';
import { useFocusedTestRunStatus } from '@/modules/automation/hooks/useFocusedTestRunStatus';
import { deriveDocumentRunningState } from '@/modules/automation-run/utils/document-running-state';
import { isRunInTerminalState } from '@/modules/automation-run/utils/run-utils';
import { runAgainInputsFrom } from '@/modules/automation/utils/run-again-state';
import { findReportedRunStep } from '@/modules/automation/utils/reported-run-step';
import {
  AutomationDocumentStatus,
  AutomationDocumentTitle,
  DocumentTitleMenu,
  type TAutomationRunningState,
} from '@/modules/automation/components/AutomationDocumentTitleBar';
import { useUpgradeAutomationManifest } from '@/modules/automation/hooks/useUpgradeAutomationManifest';
import { isManifestStale } from '@/modules/automation/utils/code-manifest';
import { useStepConnectionIcons } from '@/modules/aop-map';
import { useAutomationCredentials } from '@/modules/automation/hooks/use-automation-credentials';
import { extractOutputsValue } from '@/modules/outputs/utils';
import {
  AutomationInputsSheet,
  InputsTriggerButton,
  buildRunInputs,
  isValueEmpty,
  isBooleanType,
  isNoneType,
  unwrapType,
} from '@/modules/automation-inputs';
import {
  buildCombinedContent,
  useAutomationEditor,
} from '@/modules/automation-run/hooks/useAutomationEditor';
import { useRunControl } from '@/modules/automation-run/hooks/useRunControl';
import {
  createTextAnchor,
  SelectionHighlightExtension,
  type ISelectionHighlightAnchor,
} from '@/modules/automation-run/extensions';
import { useRainThread } from '@/modules/rain';
import { AutomationEditorChat } from '../AutomationEditorChat';
import {
  BLUEPRINT_ARTIFACT_NAME,
  BLUEPRINT_ARTIFACT_URI_ENCODED,
  RAIN_QUILL_AGENT_ID,
} from '@/modules/rain/agents/quill';
import { buildRainUserMessage } from '@/modules/rain/utils/message';
import { useAddIntegrationToChat } from '@/modules/automation/hooks/use-add-integration-to-chat';
import { captureException } from '@/modules/sentry';
import {
  selectClearCreatedDraftAutomation,
  selectCreatedDraftAutomation,
  selectClearCreatedDraftThread,
  selectCreatedDraftThread,
  useCreatingDraftStore,
} from '@/modules/drafts/stores/creating-draft.store';
import { useDiffEditor } from '@/shared/text-diffs/hooks/useDiffEditor';
import { computeDiffChangeSummaries } from '@/shared/text-diffs/utils/diff-utils';
import type { IDiffChangeSummary } from '@/shared/text-diffs/utils/diff-types';
import { useElementDimensions } from '@/shared/hooks/useElementDimensions';
import { useUrlFilters } from '@/shared/hooks/useUrlFilters';
import {
  useConfigRailExpanded,
  useDraftRailCollapsed,
} from '@/shared/hooks/use-rail-preferences';
import { useSelectionContext } from '@/shared/hooks/use-selection-context';
import { parseStepReference } from '@/shared/utils/step-reference';
import {
  draftAutomationEditViewTour,
  ETourId,
  isTourCompleted,
  ManagedTourProvider,
  selectActiveTourId,
  TourTrigger,
  useTourStore,
} from '@/shared/tours';
import {
  parseResourceName,
  readPanelSize,
  UI_ACTION_DEBOUNCE_MS,
} from '@/shared/utils';
import {
  decodeBase64ToText,
  encodeTextToBase64,
} from '@/shared/utils/base64-text';
import {
  checkIsInsufficientCreditsError,
  checkIsNotFoundError,
  errorHandler,
} from '@/shared/utils/error-handler';
import { EditorContent } from '@tiptap/react';
import QuillChat from '../QuillChat/QuillChat';
import { QUILL_AGENT_NAME } from '../QuillChat/constants';
import { FEATURE_FLAGS } from '@/shared/types/features';
import { useGateValue, useStatsigClient } from '@statsig/react-bindings';

import { IDPPanelLoading } from '@/modules/idp/components/IDPPanelLoading';

const IDPInlinePanel = lazy(() =>
  import('@/modules/idp/components/IDPInlinePanel').then(m => ({
    default: m.IDPInlinePanel,
  })),
);

// Lazy: the flow chart pulls in react-flow, and this view is behind a gate the
// reader may never open.
const AopFlowDiagram = lazy(() =>
  import('@/modules/aop-map/AopFlowDiagram').then(m => ({
    default: m.AopFlowDiagram,
  })),
);

// How long the one-shot "changed text" highlight animation runs when a staged
// preview edit appears; must match the CSS animation duration in styles.css.
const DIFF_ANIMATE_MS = 1400;

// A Test click that never produces a run stops holding the draft actions after this.
const PENDING_TEST_TIMEOUT_MS = 2 * 60 * 1000;

/** Stable stand-in for a step callback the current mode has no use for. */
const noop = () => {};

// The context-chip highlight, for the editors that don't register it
// themselves (`useAutomationEditor` already does). Module-level: the diff
// editor reads its extensions once, at creation.
const SELECTION_HIGHLIGHT_EXTENSIONS = [SelectionHighlightExtension];

/** Stands in for the manifest a blueprint does not have yet, so the rail's
 *  resource blocks read as empty instead of the rail disappearing. Module
 *  scope keeps its identity stable across renders. */
const EMPTY_MANIFEST: V1CodeManifest = {};

/** Nothing picked on the map, as one stable reference. */
const NO_MAP_SELECTION: ReadonlySet<number> = new Set();

// The message the "Make deterministic" button sends. Wrapped in a <user_action>
// tag so the chat renders a clean "Make deterministic" label instead of the raw
// text (see sanitizeUserActionForDisplay), matching the sibling review_preview
// kickoff. The agent builds on the *intent* in the inner text — it does NOT
// pattern-match the tag, so the wrapper is a frontend display convention only.
const MAKE_DETERMINISTIC_MESSAGE =
  '<user_action type="make_deterministic">The blueprint looks good — ' +
  'build it into a working automation now. Ask me any clarifying questions you ' +
  'need first, then tell me what changed from the blueprint.</user_action>';

/**
 * Props for the AutomationEditor component.
 */
export interface IAutomationEditorProps {
  /** The automation/draft ID (used for both published automations and drafts) */
  automationId: string;
  /** Whether in pre-run mode (auto-executes) */
  isRunMode?: boolean;
  /** Whether this is a draft or published automation (determines navigation paths and stage) */
  isDraft?: boolean;
  /** Whether the editor should be rendered in readonly mode */
  isReadOnly?: boolean;
  /**
   * Legacy Quill path only: the first prompt to auto-send when the editor
   * loads, passed via navigation state from NewDraftChat. The Rain (Quill V2)
   * path sends its first message at the source instead, so it does not use this.
   */
  initialMessage?: string;
  /** Legacy Quill path only: resource names of files uploaded with the first message. */
  initialAttachments?: string[];
}

/** Editor root padding, in lattice's 4px spacing units. */
const DRAFT_PAGE_PADDING_UNITS = 4;
const PAGE_PADDING_UNITS = 8;
const SPACING_UNIT_PX = 4;
/** `BrowserSessionsRail` (300px) plus the 24px gap beside the panel group. */
const BROWSER_RAIL_WITH_GAP_PX = 324;
/** The asset workspace's config rail icon column (40px) plus its 16px gap. */
const CONFIG_RAIL_WITH_GAP_PX = 56;

/**
 * Shared component for automation and draft editing/running.
 *
 * Handles all state management, editor, chat, inputs sheet, and run execution
 * for both draft and published automation workflows.
 *
 * @param {IAutomationEditorProps} props - Component props
 * @returns {JSX.Element} Automation editor component
 */
export function AutomationEditor({
  automationId,
  isRunMode,
  isDraft,
  isReadOnly = false,
  initialMessage,
  initialAttachments,
}: IAutomationEditorProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('automation-inputs');
  const { t: tCommon } = useTranslation('common');
  const { t: tAutomation } = useTranslation('automation');
  const { t: tOutputs } = useTranslation('outputs');
  const {
    value: isSaveAndRunMode,
    setTrue: setIsSaveAndRunModeTrue,
    setFalse: setIsSaveAndRunModeFalse,
  } = useBoolean();

  // In the URL, so Activity's version history button opens this same panel.
  const { getFilterParam, setFilterParam } =
    useUrlFilters<typeof ASSET_VERSION_HISTORY_PARAM>();
  const isVersionHistoryOpen =
    getFilterParam(ASSET_VERSION_HISTORY_PARAM) === 'open';
  const openVersionHistory = useCallback(
    () => setFilterParam(ASSET_VERSION_HISTORY_PARAM, 'open'),
    [setFilterParam],
  );
  const closeVersionHistory = useCallback(
    () => setFilterParam(ASSET_VERSION_HISTORY_PARAM, null),
    [setFilterParam],
  );
  const {
    value: isExportDialogOpen,
    setTrue: openExportDialog,
    setFalse: closeExportDialog,
  } = useBoolean(false);
  const isAstralV2Enabled = useGateValue(FEATURE_FLAGS.ENABLE_ASTRAL_V2_IN_UI);

  // Track the selected version in version history panel
  const [selectedVersion, setSelectedVersion] =
    useState<IVersionHistoryItemData | null>(null);
  // The version immediately before the selected one (older). The diff shows
  // "what changed IN the selected version" = previous → selected, matching how
  // Google Docs version history reads each entry as its own set of changes
  // rather than a comparison against the latest. Null for the oldest version.
  const [selectedPreviousVersion, setSelectedPreviousVersion] =
    useState<IVersionHistoryItemData | null>(null);
  // The change "pinned" by clicking a sidebar change row — its left bar stays
  // lit after the mouse leaves the row (hovering another row only previews).
  // A ref (not state) since it just drives the imperative highlight, not render.
  // Cleared whenever the selected version changes or the panel closes.
  const pinnedChangeRef = useRef<IDiffChangeSummary | null>(null);

  // When true, the next time the version history panel opens it should
  // auto-select the revision right before the latest one, landing the user
  // directly on a diff — used by the post-build "See what changed" toast.
  // Reset back to false as soon as a version gets (auto-)selected, so
  // subsequent manual opens (e.g. the header button) default to the latest.
  const [autoDiffOnHistoryOpen, setAutoDiffOnHistoryOpen] = useState(false);

  const handleVersionHistorySelect = useCallback(
    (version: IVersionHistoryItemData, previous?: IVersionHistoryItemData) => {
      setSelectedVersion(version);
      setSelectedPreviousVersion(previous ?? null);
      pinnedChangeRef.current = null;
      setAutoDiffOnHistoryOpen(false);
    },
    [],
  );

  // Clears the pair when the panel drops its selection on a filter change, so the new filter can't flash a diff of the old list's pair.
  const clearVersionSelection = useCallback(() => {
    setSelectedVersion(null);
    setSelectedPreviousVersion(null);
    pinnedChangeRef.current = null;
  }, []);

  // Initial filter for version history panel based on context (draft vs published)
  const initialFilter = isDraft
    ? V1AutomationStage.AUTOMATION_STAGE_UNSPECIFIED
    : V1AutomationStage.AUTOMATION_STAGE_PUBLISHED;

  // Track the version history filter for diff mode control
  // Diffs are only shown when filter is "All versions" (UNSPECIFIED)
  const [versionHistoryFilter, setVersionHistoryFilter] =
    useState<V1AutomationStage>(initialFilter);

  // Subscribing to activeTourId triggers a re-render when the editor tour
  // completes, so isTourCompleted reads the freshly-written localStorage value.
  useTourStore(selectActiveTourId);
  const isEditorTourDone = isTourCompleted(ETourId.DRAFT_EDITOR);

  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Gates the toggle and the view together: with the flag off there is no map
  // to switch to, so the Steps view is the only SOP view.
  const isAopMapEnabled = useGateValue(FEATURE_FLAGS.AOP_MAP);
  // Draft editing on the shared asset workspace: an activity bar on top, the
  // document's own heading and tabs, and the config rail on the right.
  const isConsistentLayout = useGateValue(FEATURE_FLAGS.CONSISTENT_LAYOUT);
  const isAssetWorkspace = isConsistentLayout && isDraft && !isRunMode;
  // A live automation's document. It shares the draft's layout, and its chat
  // edits the draft, creating one first when there is none.
  const isPublishedDocument = isConsistentLayout && !isDraft && !isRunMode;
  const isAssetLayout = isAssetWorkspace || isPublishedDocument;
  const { logEvent } = useStatsigClient();
  const handleOpenStepWorkbench = useCallback(
    (stepId: string) => {
      if (!currentOrganizationId || !currentWorkspaceId || !automationId)
        return;
      const path = getRoutePath('AUTOMATION_STEP_WORKBENCH', {
        orgId: currentOrganizationId,
        workspaceId: currentWorkspaceId,
        automationId,
        stepId,
      });
      navigate(`${path}?stage=${isDraft ? 'DRAFT' : 'PUBLISHED'}`);
    },
    [
      navigate,
      currentOrganizationId,
      currentWorkspaceId,
      automationId,
      isDraft,
    ],
  );

  const invokeAutomationMutation = useInvokeAutomation();
  const { mutateAsync: updateAutomationAsync } = useUpdateAutomation();
  const { mutateAsync: sendRainMessageAsync } = useAutomationSendMessage();
  const { mutateAsync: discardArtifactsAsync } =
    useAutomationDiscardArtifacts();
  const { mutateAsync: commitArtifactsAsync } = useAutomationCommitArtifacts();
  const [isSendingMakeDeterministic, setIsSendingMakeDeterministic] =
    useState(false);
  // When a build finishes and the blueprint flips to the built AOP, we briefly
  // show a "Built v{version} · just now" banner in the header. Holds the
  // just-built version string while the banner is visible; null hides it (and
  // the header) again. Cleared by a 3s timer.
  const [justBuiltVersion, setJustBuiltVersion] = useState<string | null>(null);
  // Quill's chat edits to `preview.md` land in the thread's grimoire *staging*
  // layer (uncommitted). When a turn stages such a change we fetch it here and
  // hold it as a reviewable diff against the committed preview; null means no
  // pending change. Accept commits it; Discard drops the staging.
  const [stagedPreviewMarkdown, setStagedPreviewMarkdown] = useState<
    string | null
  >(null);
  const [isResolvingPreviewDiff, setIsResolvingPreviewDiff] = useState(false);
  const updateAutomation = useAutomationStore(selectUpdateAutomation);
  const automationFromStore = useAutomationStore(selectAutomation);
  const createdDraftAutomation = useCreatingDraftStore(
    selectCreatedDraftAutomation,
  );
  const clearCreatedDraftAutomation = useCreatingDraftStore(
    selectClearCreatedDraftAutomation,
  );
  const setLoading = useAutomationStore(selectAutomationSetLoading);
  const setError = useAutomationStore(selectAutomationSetError);
  const resetAutomation = useAutomationStore(selectAutomationReset);
  const aopArtifactFromStore = useAutomationStore(selectAutomationAOP);

  // Store state and actions for inputs
  const storedInputsData = useAutomationInputsStore(
    selectAutomationInputsForId(automationId),
  );
  const setAutomationInputs = useAutomationInputsStore(
    selectSetAutomationInputs,
  );
  const getAutomationInputs = useAutomationInputsStore(
    selectGetAutomationInputs,
  );
  const clearNonDefaultInputs = useAutomationInputsStore(
    selectClearNonDefaultInputs,
  );
  const toggleDefaultInput = useAutomationInputsStore(selectToggleDefaultInput);
  const initializeDefaultInputs = useAutomationInputsStore(
    selectInitializeDefaultInputs,
  );
  const hasDefaultInputsChanged = useAutomationInputsStore(
    selectHasDefaultInputsChanged,
  );
  const syncOriginalDefaults = useAutomationInputsStore(
    selectSyncOriginalDefaults,
  );
  const setDefaultInputsMutation = useSetAutomationDefaultInputs();
  const clearQuillChat = useQuillChatStore(selectQuillChatClearAutomation);
  const getThreadId = useQuillChatStore(selectQuillChatGetThreadId);

  // IDP inline panel state
  const isIDPPanelOpen = useIDPPanelStore(selectIDPPanelIsOpen);
  const idpPanelSource = useIDPPanelStore(selectIDPPanelSource);
  const idpPanelIdpData = useIDPPanelStore(selectIDPPanelIdpData);
  const idpPanelFields = useIDPPanelStore(selectIDPPanelFields);
  const idpPanelDocumentName = useIDPPanelStore(selectIDPPanelDocumentName);
  const idpPanelReadOnly = useIDPPanelStore(selectIDPPanelReadOnly);
  const idpPanelExceptionFields = useIDPPanelStore(
    selectIDPPanelExceptionFields,
  );
  const idpPanelOnSave = useIDPPanelStore(selectIDPPanelOnSave);
  const idpPanelListItems = useIDPPanelStore(selectIDPPanelListItems);
  const closeIDPPanel = useIDPPanelStore(selectIDPPanelClose);

  // eslint-disable-next-line no-restricted-syntax -- reason: Close IDP panel on unmount / page navigation
  useEffect(() => {
    return () => {
      closeIDPPanel();
    };
  }, [closeIDPPanel]);

  // Local state for UI
  const [isInputsOpen, setIsInputsOpen] = useState(false);
  const [isTestInputsOpen, setIsTestInputsOpen] = useState(false);
  const [isTestRunning, setIsTestRunning] = useState(false);
  // Imperative handle to the mounted AutomationEditorChat's sendTestRun — the Test button
  // lives in the page header, outside AutomationEditorChat, so it can't get this via a
  // normal prop.
  const sendTestRunRef = useRef<
    ((inputs: Record<string, Commonv1Value>) => Promise<void>) | undefined
  >(undefined);
  // Stable identity so AutomationEditorChat's `useEffect(() => onReady(...), [onReady])`
  // only re-fires when `sendTestRun` itself actually changes, not on every
  // AutomationEditor render (an inline arrow here would rebuild `onReady`,
  // and therefore that effect, every time).
  const handleEditorChatReady = useCallback(
    (api: {
      sendTestRun: (inputs: Record<string, Commonv1Value>) => Promise<void>;
    }) => {
      sendTestRunRef.current = api.sendTestRun;
    },
    [],
  );
  // Which manifest-driven SOP presentation is showing: the step-by-step view
  // or the flow map. Defaults to 'steps'; irrelevant when there's no manifest
  // yet (unbuilt preview) or while diffing, both of which fall back to the
  // markdown editor regardless.
  // Opens on the map when the page was reached from one — "Run again" on a
  // run's map lands here in run mode — so the reader stays on the map.
  const [sopViewMode, setSopViewMode] = useState<TSopViewMode>(() =>
    isAopMapRequested(location.state) ? 'map' : 'steps',
  );
  // The options menu sits at the end of the title bar, apart from the title,
  // so its Rename reaches the title through this count.
  const [titleRenameRequests, setTitleRenameRequests] = useState(0);
  // The asset workspace's document tabs: the SOP views, or Test.
  const [documentTab, setDocumentTab] = useState<'document' | 'test'>(
    'document',
  );
  // The document-style draft view uses selection to coordinate the highlighted
  // row and the detail card in the right rail. Quoting remains an independent
  // explicit action, so selecting a row never changes the chat composer.
  const [mapSelectedSteps, setMapSelectedSteps] =
    useState<ReadonlySet<number>>(NO_MAP_SELECTION);
  const [selectedDraftStepNumber, setSelectedDraftStepNumber] = useState<
    number | undefined
  >(undefined);
  // The `test_automation`/`replay_run` run currently focused on the step view
  // (via a chat card's "view run" click), or undefined for the plain
  // authoring view. Not persisted — defaults to unfocused on reload/remount.
  const [focusedTestRun, setFocusedTestRun] = useState<
    { toolCallId: string; runId: string; startedAtMs?: number } | undefined
  >(undefined);
  // Set when Test is pressed and cleared once the chat focuses the new run,
  // so the banner is up from the click instead of waiting on the run id.
  const [pendingTestStartedAtMs, setPendingTestStartedAtMs] =
    useState<number>();
  // The editor stays mounted when switching between the live version and the
  // draft, so what belongs to one version starts over on the other.
  const [stateIsDraft, setStateIsDraft] = useState(isDraft);
  if (stateIsDraft !== isDraft) {
    setStateIsDraft(isDraft);
    setDocumentTab('document');
    setFocusedTestRun(undefined);
    setPendingTestStartedAtMs(undefined);
    setSelectedDraftStepNumber(undefined);
    setVersionHistoryFilter(
      isDraft
        ? V1AutomationStage.AUTOMATION_STAGE_UNSPECIFIED
        : V1AutomationStage.AUTOMATION_STAGE_PUBLISHED,
    );
  }
  // A run is viewed on the document, so viewing one leaves the Test tab.
  const handleFocusTestRun = useCallback((run: typeof focusedTestRun) => {
    setPendingTestStartedAtMs(undefined);
    setFocusedTestRun(run);
    if (run) setDocumentTab('document');
  }, []);
  const hasAutoRunAttempted = useRef(false);
  // Wraps the whole editor (chat + editor panes) so click-to-chat can locate
  // the mounted AutomationEditorChat composer's textarea from outside the rain module.
  const rootRef = useRef<HTMLDivElement>(null);
  const chatPanelRef = useRef<ImperativePanelHandle>(null);
  const chatWidthAtDragStart = useRef<number | null>(null);
  // The chat's px bounds are converted to the panel group's percentages against this.
  const { width: rootWidth } = useElementDimensions(rootRef);
  // Tracks the automation whose saved default inputs have already been
  // restored into the inputs store, so we restore exactly once per load.
  const initializedDefaultsForId = useRef<string | null>(null);

  // useResolvedAutomation wraps useQueryAutomation and decodes the
  // code_manifest.json artifact when present, returning a merged view where
  // manifest data (input_specs, marked_outputs) takes priority over the
  // top-level API fields.
  const {
    automation: resolvedAutomationFromQuery,
    isLoading: isLoadingExistingAutomation,
    isFetching: isFetchingAutomation,
    error: fetchError,
    isError: isFetchError,
    refetch: refetchAutomation,
  } = useResolvedAutomation({
    organizationId: currentOrganizationId,
    workspaceId: currentWorkspaceId,
    automationId: automationId || '',
    params: {
      // From published automation document, display latest published. In draft editing view, display the latest draft.
      stage: isDraft
        ? V1AutomationStage.AUTOMATION_STAGE_DRAFT
        : V1AutomationStage.AUTOMATION_STAGE_PUBLISHED,
    },
    query: {
      retry: false,
      retryOnMount: true,
      refetchOnMount: 'always',
      enabled: Boolean(
        automationId && currentOrganizationId && currentWorkspaceId,
      ),
      meta: {
        onSuccess: (response: AxiosResponse<V1Automation>) => {
          updateAutomation(response.data);
          setLoading(false);
        },
        onError: (error: AxiosError) => {
          captureException(error as Error, {
            tags: {
              component: 'AutomationEditor',
              action: 'queryAutomation',
              isDraft: isDraft ? 'true' : 'false',
              isRunMode: isRunMode ? 'true' : 'false',
            },
          });
          setError(tAutomation('automation.error.loadAutomation'));
        },
      },
      staleTime: 60000,
    },
  });
  const inputValues = storedInputsData?.values || {};
  // console.log('inputValues', inputValues);
  const inputErrors = storedInputsData?.errors || {};
  const defaultInputKeys = storedInputsData?.defaultInputKeys || [];

  // Resolved view: query data (manifest-aware) wins; fall back to store data
  // for components that may have updated it before the query landed.
  // A just-created draft's record, handed over by NewDraftChat. Only for THIS
  // automation, so a stale seed can never describe a different page.
  const seededDraftAutomation =
    createdDraftAutomation?.automationId === automationId
      ? createdDraftAutomation.automation
      : undefined;

  const automation =
    resolvedAutomationFromQuery ?? seededDraftAutomation ?? automationFromStore;

  // Restore saved default inputs on load, driven by the automation data rather
  // than the query's shared meta.onSuccess (which a sibling query can clobber).
  // The ref guard restores once per load so refetches don't overwrite edits,
  // and per version: the editor stays mounted when switching between them.
  const defaultInputsKey = `${automationId}:${isDraft ? 'draft' : 'live'}`;
  const loadedDefaultInputs = resolvedAutomationFromQuery?.default_inputs;
  // eslint-disable-next-line no-restricted-syntax -- reason: Sync server-provided default_inputs into the inputs store on load; a store write is a side effect and cannot run during render
  useEffect(() => {
    const defaults = loadedDefaultInputs;
    if (!automationId || !defaults || Object.keys(defaults).length === 0) {
      return;
    }
    if (initializedDefaultsForId.current === defaultInputsKey) return;
    initializedDefaultsForId.current = defaultInputsKey;
    initializeDefaultInputs(automationId, defaults);
  }, [
    automationId,
    defaultInputsKey,
    loadedDefaultInputs,
    initializeDefaultInputs,
  ]);

  // A "preview" is a draft automation carrying a blueprint/aop.md artifact
  // (grimoire://blueprint/aop.md to quill) that hasn't been built yet (no code).
  // english_code (aop.md) is canonical — generated from SPy at build time —
  // so the preview lives as a separate asset in the artifacts map. Seeded
  // instantly from a process-catalog template (see useCreatePreviewFromTemplate)
  // or drafted by quill before it writes any SPy. Scoped to Quill V2 — legacy
  // Quill drafts don't have a preview stage. Declared before aopArtifact below
  // so it can serve as the "what does this automation look like right now"
  // fallback used by the version-history diff when nothing has been built yet.
  const previewArtifactBase64 =
    automation?.artifacts?.[BLUEPRINT_ARTIFACT_NAME];
  const previewMarkdown = useMemo(() => {
    if (!previewArtifactBase64) return undefined;
    try {
      return decodeBase64ToText(previewArtifactBase64);
    } catch {
      return undefined;
    }
  }, [previewArtifactBase64]);

  // Derive aopArtifact from automation (query data takes precedence)
  const aopArtifact = automation?.english_code ?? aopArtifactFromStore;

  // Quill V2 automations embed the overview in the AOP itself, so we render the
  // AOP without the "Overview"/"Execution Steps" section headers.
  const includeSectionHeaders =
    automation?.kind !== V1AutomationKind.AUTOMATION_KIND_QUILL2;

  // Version-history list items carry metadata only — the list endpoint returns
  // lightweight summaries so its response stays under the API gateway's
  // transcoding limit. The content of the one or two revisions actually on
  // screen is fetched per revision here.
  const {
    selected: selectedVersionContent,
    previous: previousVersionContent,
    isLoading: isRevisionContentLoading,
  } = useRevisionContents({
    automationId,
    revisionId: selectedVersion?.revisionId,
    previousRevisionId: selectedPreviousVersion?.revisionId,
  });

  // The version-history diff compares each version against its predecessor —
  // "what changed IN this version" (Google-Docs style) — so both sides come
  // from revision snapshots (englishCode, which decodes preview.md for
  // preview-stage revisions). The current live automation / preview is NOT a
  // side here; that's the separate editable-preview mode.
  const selectedVersionCombinedContent = useMemo(
    () =>
      buildCombinedContent(
        selectedVersionContent.description,
        selectedVersionContent.englishCode,
        includeSectionHeaders,
      ),
    [
      selectedVersionContent.description,
      selectedVersionContent.englishCode,
      includeSectionHeaders,
    ],
  );
  // Baseline: the previous version's content. Empty for the oldest version
  // (nothing precedes it) → its diff reads as "everything added".
  const previousVersionCombinedContent = useMemo(
    () =>
      buildCombinedContent(
        previousVersionContent.description,
        previousVersionContent.englishCode,
        includeSectionHeaders,
      ),
    [
      previousVersionContent.description,
      previousVersionContent.englishCode,
      includeSectionHeaders,
    ],
  );

  // Only show diff when:
  // 1. A version is selected (its predecessor may be empty — the oldest
  //    revision predates any content — and diffing against empty is a valid
  //    "everything was added" view, which should render instead of a blank pane).
  // 2. Both revisions' content has resolved. Without this, a selected version
  //    that lands before its predecessor would diff against an empty baseline
  //    and flash an all-green "everything was added" view for a frame.
  // 3. At least one side actually has content (both empty = nothing to show).
  // 4. The version's content differs from its predecessor.
  // 5. Filter is "All versions" or Published (document view). Draft-only
  //    still hides diffs. Published is a consecutive published list, so the
  //    previous→selected comparison is the track-changes view.
  const isDiffMode = Boolean(
    selectedVersion &&
      !isRevisionContentLoading &&
      (selectedVersionCombinedContent || previousVersionCombinedContent) &&
      selectedVersionCombinedContent !== previousVersionCombinedContent &&
      versionHistoryShowsTrackChanges(versionHistoryFilter),
  );

  // Single gate for the document pane: it has nothing meaningful to render
  // while either the live automation or a selected revision's content is in
  // flight. Without the revision half, selecting a version would briefly show
  // the live AOP before snapping into the diff.
  const isEditorContentLoading =
    isFetchingAutomation || isRevisionContentLoading;

  // A Quill V2, not-yet-built automation — eligible to carry a preview.md,
  // whether or not one has been committed yet. Used to gate whether a staged
  // quill write should even be looked at (handleQuillArtifactStaged below),
  // independent of hasPreviewContent's stronger "already has committed
  // content" check.
  const isUnbuiltQuillV2 = Boolean(
    automation?.kind === V1AutomationKind.AUTOMATION_KIND_QUILL2 &&
      (automation?.code_length ?? 0) === 0,
  );

  // A Quill V2 draft that carries a preview.md and hasn't been built yet has
  // content worth showing a pane for, independent of whether a version is
  // currently selected in history. Kept separate from isPreviewAutomation
  // (below) so selecting a version in history doesn't collapse showEditor and
  // unmount the diff pane — the whole reason preview-draft diffs were invisible.
  const hasPreviewContent = Boolean(
    isUnbuiltQuillV2 && previewMarkdown && previewMarkdown.length > 0,
  );

  // The editable-preview mode itself is only active when NOT viewing a
  // historical version — while a version is selected the pane shows a
  // read-only diff, not the editable preview.
  const isPreviewAutomation = hasPreviewContent && !selectedVersion;

  const isQuillV2Automation =
    automation?.kind === V1AutomationKind.AUTOMATION_KIND_QUILL2;

  const isChatVisible =
    !isRunMode && (!isReadOnly || isPublishedDocument) && !isVersionHistoryOpen;

  // The chat surface is decided purely by the automation kind: a Quill V2
  // automation (isQuillV2Automation) always uses the Rain chat, everything else
  // uses the legacy Quill chat. The thread id is resolved separately (existing
  // or lazily created) so its presence never gates which chat we render.
  // Set only by useUpgradeAutomationManifest just before navigating here —
  // seeds the chat's thread on this mount's first render so it can never
  // lose the "which thread is latest" race against its own lazy-create
  // effect (see useRainThread's initialThreadId doc).
  const pendingRainThreadIdFromRouter = (
    location.state as { pendingRainThreadId?: string } | null
  )?.pendingRainThreadId;

  // The same seed for drafts created from the drafts hero, which cannot use
  // router state: the draft edit page clears `location.state` on mount and this
  // editor is lazy, so it can first render after the clear has already run.
  // Guarded on automationId so a seed left over from another draft is ignored.
  const createdDraftThread = useCreatingDraftStore(selectCreatedDraftThread);
  const clearCreatedDraftThread = useCreatingDraftStore(
    selectClearCreatedDraftThread,
  );
  const pendingRainThreadId =
    pendingRainThreadIdFromRouter ??
    (createdDraftThread?.automationId === automationId
      ? createdDraftThread.threadId
      : undefined);

  // Consumed — drop it so a later mount cannot bind to a stale thread. The read
  // above already happened during this render, so useRainThread has its seed
  // before this effect runs.
  // eslint-disable-next-line no-restricted-syntax -- reason: one-shot consume of a hand-off seed after the first render that used it
  useEffect(() => {
    if (createdDraftThread?.automationId === automationId) {
      clearCreatedDraftThread();
    }
    // Only once the real record is in hand — dropping it while the query is
    // still running would fall back to nothing and re-show the spinner the
    // seed exists to prevent.
    if (
      resolvedAutomationFromQuery &&
      createdDraftAutomation?.automationId === automationId
    ) {
      clearCreatedDraftAutomation();
    }
  }, [
    createdDraftThread,
    automationId,
    clearCreatedDraftThread,
    resolvedAutomationFromQuery,
    createdDraftAutomation,
    clearCreatedDraftAutomation,
  ]);

  const {
    rainThreadId,
    setSelectedRainThreadId,
    hasRainThreadError,
    retryResolveRainThread,
  } = useRainThread({
    automationId,
    enabled: isQuillV2Automation && isChatVisible,
    initialThreadId: pendingRainThreadId,
  });

  // The live version's draft, if any: the version select needs it, and the
  // live chat forks one before sending.
  const liveDraftQueryKey = getQueryAutomationQueryKey(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    { stage: V1AutomationStage.AUTOMATION_STAGE_DRAFT },
  );
  const {
    data: liveDraftData,
    error: liveDraftError,
    isLoading: isLiveDraftLoading,
    refetch: refetchLiveDraft,
  } = useQueryAutomation(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    { stage: V1AutomationStage.AUTOMATION_STAGE_DRAFT },
    {
      query: {
        enabled: Boolean(
          isPublishedDocument &&
            automationId &&
            currentOrganizationId &&
            currentWorkspaceId,
        ),
        retry: false,
      },
    },
  );
  // Only a 404 means there's no draft, and it has to win over the data a
  // failed refetch keeps from before a discard.
  const isLiveDraftMissing = checkIsNotFoundError(liveDraftError);
  const liveDraftVersion = isLiveDraftMissing
    ? undefined
    : liveDraftData?.data.version;
  const hasLiveDraft = Boolean(liveDraftVersion);
  const { mutateAsync: forkPublishedAutomation } = useForkPublishedAutomation();

  // A message on the live version asks for a change, which lands in the draft:
  // fork one if there's none, then switch to it while the chat, which stays
  // mounted, sends the message on the same thread.
  const handleLiveBeforeSend = useCallback(async () => {
    // Forking over a draft that exists would replace it, so it has to be known.
    const result =
      isLiveDraftLoading || (liveDraftError && !isLiveDraftMissing)
        ? await refetchLiveDraft()
        : undefined;
    if (result?.error && !checkIsNotFoundError(result.error)) {
      toast.error(tAutomation('automation.draft.create.error'));
      return false;
    }
    const draftExists = result
      ? !result.error && Boolean(result.data?.data.version)
      : hasLiveDraft;
    if (!draftExists) {
      try {
        await forkPublishedAutomation({
          organization: currentOrganizationId,
          workspace: currentWorkspaceId,
          automation: automationId,
          data: {},
        });
      } catch (error) {
        toast.error(tAutomation('automation.draft.create.error'));
        captureException(error as Error, {
          tags: { component: 'AutomationEditor', action: 'forkFromLiveChat' },
        });
        return false;
      }
      [
        getListAutomationsQueryKey,
        getListAutomationSummariesQueryKey,
        getListAutomationsInfiniteQueryKey,
        getListAutomationSummariesInfiniteQueryKey,
      ].forEach(getKey =>
        queryClient.invalidateQueries({
          queryKey: getKey(currentOrganizationId, currentWorkspaceId),
        }),
      );
      void queryClient.invalidateQueries({ queryKey: liveDraftQueryKey });
    }
    // Replaced, as the version switch does: it's the same automation, so Back
    // leaves it rather than returning to the live version.
    navigate(
      getRoutePath('DRAFT_EDITOR', {
        orgId: currentOrganizationId,
        workspaceId: currentWorkspaceId,
        draftId: automationId,
      }),
      { replace: true },
    );
    return true;
  }, [
    isLiveDraftLoading,
    liveDraftError,
    isLiveDraftMissing,
    refetchLiveDraft,
    hasLiveDraft,
    forkPublishedAutomation,
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    queryClient,
    liveDraftQueryKey,
    navigate,
    tAutomation,
  ]);

  // Build state comes off the thread's event log, not component state: a build
  // spans several quill turns, so a reload would otherwise drop it while the
  // build kept running. `isSendingMakeDeterministic` bridges the gap between
  // the request POST resolving and the refetched log carrying its echo.
  const buildPhase = useBlueprintBuildPhase({
    automationId,
    threadId: rainThreadId ?? null,
    hasBuiltCode: (automation?.code_length ?? 0) > 0,
    enabled: hasPreviewContent,
  });
  const isBuildingBlueprint =
    hasPreviewContent &&
    (buildPhase === 'building' || isSendingMakeDeterministic);

  // A build that ended before writing any code. The blueprint is editable again
  // and another run is offered, but the bar says what happened rather than
  // reverting to a plain "not built yet" that hides the attempt.
  const endedBuildPhase =
    isPreviewAutomation && !isBuildingBlueprint && buildPhase === 'stopped'
      ? 'stopped'
      : isPreviewAutomation && !isBuildingBlueprint && buildPhase === 'failed'
        ? 'failed'
        : null;

  // Detect the moment a build finishes: the pane was showing a building
  // blueprint and now carries real code. Tracking the previous building state
  // (rather than a "did I click it" flag) means the banner also lands for a
  // build the user reloaded into.
  const wasBuildingRef = useRef(isBuildingBlueprint);
  // eslint-disable-next-line no-restricted-syntax -- reason: detect the building→built transition; a store/render can't express this edge
  useEffect(() => {
    const wasBuilding = wasBuildingRef.current;
    wasBuildingRef.current = isBuildingBlueprint;
    // Same predicate the build phase itself uses. Reading `english_code` here
    // instead would drop the banner whenever `code_length` lands first: the
    // pane would leave the building state on a render where this still saw no
    // code, clearing `wasBuilding` before the transition could fire.
    const hasBuiltCode = (automation?.code_length ?? 0) > 0;
    if (wasBuilding && !isBuildingBlueprint && hasBuiltCode) {
      // Only show the banner when we actually have a version — falling back to
      // '' would render a malformed "Built v" if version lags code in the API
      // response, so suppress the banner in that (unlikely) race instead.
      setJustBuiltVersion(automation?.version || null);
    }
  }, [isBuildingBlueprint, automation?.code_length, automation?.version]);

  // Auto-dismiss the "just built" banner after 3s. Kept in its own effect keyed
  // only on justBuiltVersion so the timer's lifecycle isn't tied to the
  // detection effect above.
  // eslint-disable-next-line no-restricted-syntax -- reason: one-shot dismissal timer for a transient banner
  useEffect(() => {
    if (justBuiltVersion === null) return;
    const timer = window.setTimeout(() => setJustBuiltVersion(null), 3000);
    return () => window.clearTimeout(timer);
  }, [justBuiltVersion]);

  // Quill staged an edit to preview.md this turn — show it as a reviewable diff
  // (committed vs staged) instead of the editable preview until the user accepts
  // or discards it. Guards on preview mode so a built automation never enters it.
  // Quill edits its own blueprint during builds, so defer review until it finishes.
  // The staging is still captured, so it surfaces if the build ends without code.
  const hasPendingPreviewProposal =
    hasPreviewContent &&
    !isBuildingBlueprint &&
    stagedPreviewMarkdown !== null &&
    stagedPreviewMarkdown !== previewMarkdown;
  const isPreviewDiffMode = isPreviewAutomation && hasPendingPreviewProposal;

  // `resolvedAutomationFromQuery` (not the store-merged `automation`) is the
  // one guaranteed to have gone through `resolveAutomation`, so it's the only
  // safe source for `.manifest` here. Unbuilt previews have no manifest (no
  // code yet for jarvis to analyze), so the markdown/TipTap blueprint editor
  // is all there is to show in that state.
  const manifest = resolvedAutomationFromQuery?.manifest;
  const automationConfig = resolvedAutomationFromQuery?.config;
  // Also true for a manifest that EXISTS but predates jarvis's title/
  // overview metadata (the old code_manifest.json artifact shape) — Steps
  // view has nothing to render but "Untitled step" for these, so the
  // upgrade banner below should offer to fix it the same as a missing one.
  const manifestNeedsUpgrade = !manifest || isManifestStale(manifest);

  // Reconstructs the focused test run's own CodeManifest (never the live
  // in-editor `manifest` above) and derives per-step status from its bishop
  // journal — see useTestRunManifest/useFocusedTestRunStatus.
  const {
    manifest: focusedRunManifest,
    status: focusedRunManifestStatus,
    run: focusedRun,
  } = useTestRunManifest({
    organizationId: currentOrganizationId,
    workspaceId: currentWorkspaceId,
    automationId,
    runId: focusedTestRun?.runId,
  });

  // `run` here supersedes `focusedRun` above for anything displayed to the
  // user — it starts as that same GetRun snapshot but is kept current by
  // `run_update` events on the bishop journal stream, whereas `focusedRun`
  // itself is fetched once and never changes. Using the stale snapshot here
  // would show all steps done while the banner/outputs stay stuck pending.
  const {
    statuses: focusedRunStepStatuses,
    stepIO: focusedRunStepIO,
    runningStepCalls: focusedRunRunningStepCalls,
    run: focusedRunLive,
    stepDetailsUnavailable: focusedRunStepDetailsUnavailable,
    eventsError: focusedRunEventsError,
  } = useFocusedTestRunStatus({
    automationId,
    runId: focusedTestRun?.runId,
    manifest: focusedRunManifest,
    run: focusedRun,
  });

  // The live run state for the document title bar, off the focused test run
  // the step view and TestRunBanner already read — no second data path.
  //
  // The step is only ever one the run actually reported: `focusedRunStepStatuses`
  // is derived from the bishop journal, so a queued run (or the window before
  // its journal arrives) has no 'running'/'paused' entry at all and
  // `findReportedRunStep` yields null. `deriveDocumentRunningState` then renders
  // the state without naming a step, rather than claiming "Step 1 of N" for work
  // that has not started. See both utils for the step-naming rules.
  const documentRunning = useMemo<TAutomationRunningState | undefined>(() => {
    if (!focusedTestRun) return undefined;

    return deriveDocumentRunningState({
      // `deriveDocumentRunningState` only counts steps, and V1Step's `number`
      // is optional where ICodeManifestStep's is not, so project rather than
      // widening the shared util's contract.
      codeManifest: {
        steps: (focusedRunManifest?.steps ?? []).map((step, index) => ({
          number: step.number ?? index + 1,
        })),
      },
      reportedStep: findReportedRunStep(focusedRunStepStatuses),
      isAwaitingGuidance: !!focusedRunLive?.state?.awaiting_guidance,
      isTerminal: isRunInTerminalState(focusedRunLive?.state ?? undefined),
    });
  }, [
    focusedTestRun,
    focusedRunStepStatuses,
    focusedRunManifest?.steps,
    focusedRunLive?.state,
  ]);

  // Pause/resume for the focused run, shared with the run page header. No
  // refetch is threaded in as `onSuccess`: `focusedRunLive` above already
  // tracks the state transition off the same bishop `run_update` stream, so
  // the banner badge and step statuses follow the pause on their own.
  const {
    action: focusedRunControlAction,
    isPending: isFocusedRunControlPending,
    isPausing: isFocusedRunPausing,
    isDisabled: isFocusedRunControlDisabled,
    control: handleFocusedRunControl,
  } = useRunControl({
    automationId,
    runId: focusedTestRun?.runId,
    runState: focusedRunLive?.state,
    pauseReason: tAutomation('automation.manifestSopView.testRun.pauseReason'),
  });

  // The focused run's real top-level input/output values, keyed by name —
  // rendered as a Value column directly on ManifestSopView's Inputs/Outputs
  // schema tables instead of a second, separate block (see TestRunBanner).
  const focusedRunInputValues = useMemo(
    () => extractOutputsValue(focusedRunLive?.user_inputs ?? {}),
    [focusedRunLive?.user_inputs],
  );
  const focusedRunOutputValues = useMemo(() => {
    const outputs = focusedRunLive?.state?.completed?.outputs;
    return outputs ? extractOutputsValue(outputs) : {};
  }, [focusedRunLive?.state?.completed?.outputs]);

  /* Handed to the document and the SOP view as props. Stable so the memoised
   * step rows are not re-rendered by every unrelated editor render. */
  const getFocusedRunStepStatus = useCallback(
    (stepNumber: number) => focusedRunStepStatuses.get(stepNumber),
    [focusedRunStepStatuses],
  );
  const getFocusedRunStepIO = useCallback(
    (stepNumber: number) => focusedRunStepIO.get(stepNumber),
    [focusedRunStepIO],
  );
  const getFocusedRunInputValue = useCallback(
    (name: string) => focusedRunInputValues[name],
    [focusedRunInputValues],
  );
  const getFocusedRunOutputValue = useCallback(
    (name: string) => focusedRunOutputValues[name],
    [focusedRunOutputValues],
  );

  // Same hook, params and filter shape as `AutomationIntegrationsPanel`, so
  // this hits the cached query rather than a second round-trip. Resolves which
  // book/icon a step's external call belongs to.
  const credentialsByAlias = useAutomationCredentials({
    automationId,
    isDraft,
  });

  // What the step-level surfaces draw: a focused test run's own reconstructed
  // manifest once it is ready, the live one otherwise. `test_automation` runs
  // staged code with no version pointer, so a run's step structure can differ
  // from what the editor holds now — and `focusedRunStepStatuses`/`stepIO` are
  // keyed on the run's numbering, not the editor's. The Steps view, the right
  // rail and the map all read this one so none of them can pair a run's
  // per-step state with a manifest it did not come from.
  const draftDisplayManifest =
    focusedTestRun && focusedRunManifestStatus === 'ready'
      ? (focusedRunManifest ?? manifest)
      : manifest;

  // Gated on the live manifest, which is what the toggle's availability
  // follows: an automation with nothing built has no map to offer, whatever a
  // focused run of it once looked like.
  const canShowAopMapView =
    isAopMapEnabled &&
    !isDiffMode &&
    !isPreviewDiffMode &&
    !!manifest &&
    !isManifestStale(manifest);
  const showAopMapView = canShowAopMapView && sopViewMode === 'map';
  const toggleSopViewMode = () => {
    const next: TSopViewMode = sopViewMode === 'map' ? 'steps' : 'map';
    setSopViewMode(next);
    // Coming back to the map shouldn't reopen the rail on an old pick.
    if (next !== 'map') setMapSelectedSteps(NO_MAP_SELECTION);
    logEvent('automation_editor_sop_view_changed', next, {
      automation_id: automationId,
      is_draft: String(isDraft),
    });
  };

  // A focused test run carries its own reconstructed manifest
  // (`focusedRunManifest`, built by jarvis from that run's own
  // `executable_steps` — see `useTestRunManifest`), independent of whether
  // the live automation itself has been built yet. So "View Run" must still
  // open the step view for an unbuilt blueprint (no live `manifest`) as long
  // as that run's own manifest is ready — everything else about the
  // blueprint/markdown editor (the export menu, Approve & Build, etc.) is
  // untouched and stays gated on the live `manifest` as before.
  //
  // The only choice on offer is Steps vs Map; the markdown editor is reachable
  // from neither.
  const showManifestSopView =
    !showAopMapView &&
    !isDiffMode &&
    !isPreviewDiffMode &&
    ((!!manifest && !isManifestStale(manifest)) ||
      (!!focusedTestRun && focusedRunManifestStatus === 'ready'));

  // From the Test click until the run ends; the draft actions stay disabled
  // throughout. A focused run whose state hasn't loaded counts as in progress.
  // A pending test only counts where its banner (and close button) shows.
  const isTestInProgress =
    isTestRunning ||
    (pendingTestStartedAtMs !== undefined && showManifestSopView) ||
    (!!focusedTestRun &&
      !isRunInTerminalState(focusedRunLive?.state ?? undefined));

  // eslint-disable-next-line no-restricted-syntax -- reason: timer with cleanup
  useEffect(() => {
    if (pendingTestStartedAtMs === undefined) return;
    const timeout = setTimeout(
      () => setPendingTestStartedAtMs(undefined),
      pendingTestStartedAtMs + PENDING_TEST_TIMEOUT_MS - Date.now(),
    );
    return () => clearTimeout(timeout);
  }, [pendingTestStartedAtMs]);

  // Which integrations each step reaches, resolved through the automation's
  // connections to the books that carry the icons. Declared here rather than
  // inside the diagram so it reuses `credentialsByAlias`, which the Steps view
  // and the Connections tab already build from the same connections query.
  //
  // Withheld unless the map is reachable: the icons are the map's alone, and
  // the manifest is what turns into one book request per integration. The
  // credentials query itself has to stay on for the Steps view.
  //
  // Keyed on the same manifest the tiles are drawn from, since these are
  // step-numbered too.
  const aopConnectionIcons = useStepConnectionIcons({
    manifest: canShowAopMapView ? draftDisplayManifest : undefined,
    credentialsByAlias,
  });

  // Layout only: the map is a wide pannable canvas, so it takes the chat
  // panel's half of the split. Deliberately separate from `isChatVisible`,
  // which the thread query and the Test button stay on — switching views must
  // not stand those down. The chat unmounts on the way (the panel group is
  // keyed per layout), so its local state and its event stream both restart
  // when the reader comes back; everything either carries is server-backed.
  // In the asset workspace the map is a document tab, so the chat stays.
  // A live legacy automation keeps no chat: only quill2 can hand a message to a draft.
  const isChatPanelOpen =
    isChatVisible &&
    (isAssetLayout || !showAopMapView) &&
    (!isPublishedDocument || isQuillV2Automation);
  const hasBrowserSessions = useSyncExternalStore(
    subscribeToBrowserPanel,
    () => getBrowserPanelState().sessionIds.length > 0,
  );

  // `automation` gets a new object reference on every refetch — including the
  // refetch a save itself triggers — so closing over it directly would churn
  // `savePreviewMarkdown`'s identity, and with it every review callback built
  // on top of it, on each save cycle. Reading it from a ref keeps them stable.
  const automationRef = useRef(automation);
  automationRef.current = automation;

  // Same reason: read inside savePreviewMarkdown rather than closing over
  // rainThreadId, so the thread resolving doesn't rebuild the callback.
  const rainThreadIdRef = useRef<string | null>(null);
  rainThreadIdRef.current = rainThreadId ?? null;

  /**
   * Persists blueprint markdown to the preview.md artifact. Only reached from
   * the staged-review flow (accepting quill's proposal, or committing a brand
   * new draft's first write) — the pane itself is read-only — so this never
   * touches english_code (which quill generates from SPy) or the built
   * automation's code/code_manifest. Existing artifacts are merged in so the
   * write can't clobber siblings (e.g. context.md).
   *
   * Resolves to whether the write landed. Failures are handled here (toast +
   * Sentry) rather than thrown, so callers that chain further state changes on a
   * successful commit must check the result before closing the proposal.
   */
  const savePreviewMarkdown = useCallback(
    async (markdown: string): Promise<boolean> => {
      const currentAutomation = automationRef.current;
      const parsedId = currentAutomation?.name
        ? parseResourceName(currentAutomation.name).automation
        : automationId;
      if (!parsedId) return false;
      const threadId = rainThreadIdRef.current;
      try {
        if (threadId) {
          // Single write lifecycle: stage the content into the thread's
          // grimoire overlay, then commit it. Every preview write — first-draft
          // and accepting a quill edit — goes through the SAME thread the agent
          // uses, so the thread's base snapshot never goes stale relative to an
          // out-of-band write. quill then reads back exactly what was committed
          // (no more "UI shows X, quill thinks Y").
          await automationUpdateArtifact(
            currentOrganizationId,
            currentWorkspaceId,
            parsedId,
            RAIN_QUILL_AGENT_ID,
            threadId,
            BLUEPRINT_ARTIFACT_URI_ENCODED,
            {
              mime_type: 'text/markdown',
              data: encodeTextToBase64(markdown),
            },
          );
          await commitArtifactsAsync({
            organization: currentOrganizationId,
            workspace: currentWorkspaceId,
            automation: parsedId,
            agentId: RAIN_QUILL_AGENT_ID,
            threadId,
            data: {},
          });
        } else {
          // No thread yet (e.g. a catalog template seeding the preview before
          // the editor lazily creates the thread). Nothing has captured a base
          // snapshot to go stale, so writing the committed artifact directly is
          // safe here.
          await updateAutomationAsync({
            organization: currentOrganizationId,
            workspace: currentWorkspaceId,
            automation: parsedId,
            data: {
              display_name: currentAutomation?.display_name ?? '',
              artifacts: {
                ...currentAutomation?.artifacts,
                [BLUEPRINT_ARTIFACT_NAME]: encodeTextToBase64(markdown),
              },
            },
          });
        }
        // Refresh the automation query so the newly committed blueprint
        // artifact flows into previewMarkdown → the editor. Without this the
        // freshly-created blueprint only appears after a manual reload, since
        // React Query has no reason to refetch (staleTime: 60000).
        await refetchAutomation();
        return true;
      } catch (error) {
        errorHandler(error, () => {
          toast.error(tAutomation('automation.preview.error.saveFailed'));
          captureException(error as Error, {
            tags: {
              component: 'AutomationEditor',
              action: 'savePreviewMarkdown',
            },
          });
        });
        return false;
      }
    },
    [
      automationId,
      commitArtifactsAsync,
      currentOrganizationId,
      currentWorkspaceId,
      refetchAutomation,
      tAutomation,
      updateAutomationAsync,
    ],
  );

  /**
   * Persists a single business-tunable config edit from the manifest SOP
   * view's Config tab. Sends the whole config map back (not just the edited
   * key) since `UpdateAutomation`'s `config` field replaces rather than
   * merges — a partial map here would silently drop every other config.
   * Only the label of the edited entry is preserved; the value is the one
   * new thing being written.
   */
  const saveConfigValue = useCallback(
    async (name: string, value: Commonv1Value) => {
      const currentAutomation = automationRef.current;
      const parsedId = currentAutomation?.name
        ? parseResourceName(currentAutomation.name).automation
        : automationId;
      if (!parsedId) return;

      const nextConfig: V1AutomationConfig = {
        ...currentAutomation?.config,
        [name]: { ...currentAutomation?.config?.[name], value },
      };

      try {
        await updateAutomationAsync({
          organization: currentOrganizationId,
          workspace: currentWorkspaceId,
          automation: parsedId,
          data: {
            display_name: currentAutomation?.display_name ?? '',
            config: nextConfig,
          },
        });
        await refetchAutomation();
      } catch (error) {
        errorHandler(error, () => {
          captureException(error as Error, {
            tags: {
              component: 'AutomationEditor',
              action: 'saveConfigValue',
            },
          });
        });
        throw error;
      }
    },
    [
      automationId,
      currentOrganizationId,
      currentWorkspaceId,
      refetchAutomation,
      updateAutomationAsync,
    ],
  );

  // Normal editor for regular view (no diffs to be computed)
  // Use selected version's description if available, otherwise fall back to current automation description.
  // In preview mode the content is the preview.md artifact, not the (empty) AOP.
  const defaultEditor = useAutomationEditor({
    aopCode:
      selectedVersionContent.englishCode ??
      (isPreviewAutomation ? previewMarkdown : aopArtifact),
    description: selectedVersionContent.description ?? automation?.description,
    includeSectionHeaders,
    // Read-only in every state, blueprint included: quill re-reads and rewrites
    // preview.md on its own turns, so anything typed straight into the pane is
    // discarded the next time it writes. The chat is the only way in.
    editable: false,
  });

  // Diff editor for version comparison view (only created when needed).
  // Direction: the PREVIOUS version is the baseline (oldCode) and the SELECTED
  // version is the new state (newCode), so the diff reads "what changed in this
  // version" — additions are content this version introduced, deletions are
  // content it removed. Swapping these reverses the green/added and red/deleted
  // marks. Uses combined content (description + AOP) to diff both sections.
  const diffEditor = useDiffEditor({
    oldCode: previousVersionCombinedContent,
    newCode: selectedVersionCombinedContent,
    enabled: isDiffMode,
  });

  // Navigable list of the changes the diff introduces, for the version-history
  // sidebar. Computed from the SAME (old, new) inputs and direction as
  // diffEditor above, so each summary's index maps to the matching
  // `data-diff-anchor` stamped on the rendered block.
  const changeSummaries = useMemo(
    () =>
      isDiffMode
        ? computeDiffChangeSummaries(
            previousVersionCombinedContent,
            selectedVersionCombinedContent,
          )
        : [],
    [
      isDiffMode,
      previousVersionCombinedContent,
      selectedVersionCombinedContent,
    ],
  );

  const reviewBase = previewMarkdown ?? '';

  // Match the rendered diff so counts and scroll targets share the same anchors.
  const stagedChangeSummaries = useMemo(
    () =>
      hasPendingPreviewProposal
        ? computeDiffChangeSummaries(reviewBase, stagedPreviewMarkdown ?? '')
        : [],
    [hasPendingPreviewProposal, reviewBase, stagedPreviewMarkdown],
  );

  // Diff editor for quill's staged preview change (base → staged). Enabled only
  // while reviewing, so it's a no-op in the common (no pending change) case.
  const previewDiffEditor = useDiffEditor({
    oldCode: reviewBase,
    newCode: stagedPreviewMarkdown ?? '',
    enabled: isPreviewDiffMode,
    // Carries the chat's context-chip highlights into the review, so a
    // selection the user made before quill proposed its change stays visible
    // while they read the change.
    additionalExtensions: SELECTION_HIGHLIGHT_EXTENSIONS,
  });

  // Wrapper around the diff editor's scrollable content, so a sidebar change
  // row can scroll the document to its block via the data-diff-anchor marker.
  const diffScrollRef = useRef<HTMLDivElement>(null);

  /**
   * Highlights the block(s) a change covers and, on click, scrolls to the
   * first one. A collapsed section entry covers indices `index..endIndex`, so
   * every block in that range is highlighted — hovering the section row lights
   * up the whole section on the left, not just its heading. Hover applies the
   * highlight without the smooth-scroll jump so passing over rows doesn't
   * thrash; a click scrolls the first covered block into view.
   */
  const revealDiffChange = useCallback(
    (
      change: IDiffChangeSummary | null,
      options?: { scroll?: boolean; block?: ScrollLogicalPosition },
    ) => {
      const container = diffScrollRef.current;
      if (!container) return;
      // Clear any prior highlight first.
      container
        .querySelectorAll('[data-diff-anchor].diff-anchor-active')
        .forEach(el => el.classList.remove('diff-anchor-active'));
      if (!change) return;
      let firstTarget: HTMLElement | null = null;
      for (let k = change.index; k <= change.endIndex; k += 1) {
        const el = container.querySelector<HTMLElement>(
          `[data-diff-anchor="${k}"]`,
        );
        if (!el) continue;
        el.classList.add('diff-anchor-active');
        if (!firstTarget) firstTarget = el;
      }
      if (options?.scroll && firstTarget) {
        firstTarget.scrollIntoView({
          behavior: 'smooth',
          block: options.block ?? 'start',
        });
      }
    },
    [],
  );

  // Hover just previews a change's bar (no scroll — scrolling on hover made
  // the diff jump around). Leaving (null) falls back to the pinned (clicked)
  // change so a clicked bar stays lit while you mouse around.
  const handleChangeActivate = useCallback(
    (change: IDiffChangeSummary | null) =>
      revealDiffChange(change ?? pinnedChangeRef.current, { scroll: false }),
    [revealDiffChange],
  );
  // Clicking pins the change (its bar persists) and scrolls it into view.
  const handleChangeSelect = useCallback(
    (change: IDiffChangeSummary) => {
      pinnedChangeRef.current = change;
      revealDiffChange(change, { scroll: true, block: 'center' });
    },
    [revealDiffChange],
  );

  // Tracks the changes we've already revealed (by content, not anchor index —
  // indices renumber when a change lands mid-document) so each staged update
  // can scroll to the change(s) added since last time, not back to the first.
  const seenChangeKeysRef = useRef<Set<string>>(new Set());

  // Whenever a staged preview edit appears OR updates (changes pouring in as
  // quill edits the preview), scroll the MOST-RECENTLY-ADDED change into view
  // and play a cascading highlight — so the newest edit visibly lands
  // (Claude-style) instead of silently swapping content or snapping back to the
  // first change. Keyed on stagedPreviewMarkdown so it re-runs for each new set
  // of changes, and it waits for the diff editor to actually render the anchors
  // before scrolling (setContent is async — the anchors aren't in the DOM the
  // instant the mode flips), which is why a one-shot on mode-flip missed it.
  // eslint-disable-next-line no-restricted-syntax -- reason: imperative scroll + cascading highlight that must wait for async diff render
  useEffect(() => {
    if (!isPreviewDiffMode || stagedPreviewMarkdown == null) {
      // Diff session closed — forget shown changes so the next one starts fresh.
      seenChangeKeysRef.current = new Set();
      return;
    }
    const container = diffScrollRef.current;
    if (!container) return;

    // Identify the change added since the last render by content (status +
    // label), NOT anchor index — indices renumber when a change lands
    // mid-document. Scroll to the last newly-added change; if nothing is new
    // (e.g. first appearance), fall back to the last change in the document.
    const keyOf = (s: IDiffChangeSummary) => `${s.status}:${s.label}`;
    const seen = seenChangeKeysRef.current;
    const added = stagedChangeSummaries.filter(s => !seen.has(keyOf(s)));
    // Preserve the reader's position when a refreshed proposal adds no changes.
    if (added.length === 0 && seen.size > 0) {
      seenChangeKeysRef.current = new Set(stagedChangeSummaries.map(keyOf));
      return;
    }
    const target =
      added.length > 0
        ? added[added.length - 1]
        : stagedChangeSummaries[stagedChangeSummaries.length - 1];
    seenChangeKeysRef.current = new Set(stagedChangeSummaries.map(keyOf));

    const STAGGER_STEP = 90;
    const STAGGER_CAP = 10;
    let raf = 0;
    let cleanupTimer = 0;
    let tries = 0;

    const run = () => {
      const anchors = Array.from(
        container.querySelectorAll<HTMLElement>('[data-diff-anchor]'),
      );
      if (anchors.length === 0) {
        // Diff content not rendered yet — retry for ~0.5s before giving up.
        if (tries++ < 30) raf = requestAnimationFrame(run);
        return;
      }
      // Animate ONLY the anchors introduced by this edit — map the new change
      // summaries to their anchor elements by index; previously-shown changes
      // stay put and don't re-cascade. On first appearance every summary is
      // "added", so the whole diff animates once, as before.
      const newAnchorIndices = new Set(added.map(s => s.index));
      const newAnchors = anchors.filter(el =>
        newAnchorIndices.has(Number(el.dataset.diffAnchor)),
      );
      let maxDelay = 0;
      newAnchors.forEach((el, i) => {
        const delay = Math.min(i, STAGGER_CAP) * STAGGER_STEP;
        maxDelay = Math.max(maxDelay, delay);
        el.style.setProperty('--diff-stagger', `${delay}ms`);
        el.classList.add('diff-anchor-new');
      });
      // Scroll the newest change into view (fall back to the last rendered
      // anchor if its node isn't resolvable yet).
      const targetEl =
        (target != null &&
          container.querySelector<HTMLElement>(
            `[data-diff-anchor="${target.index}"]`,
          )) ||
        anchors[anchors.length - 1];
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      cleanupTimer = window.setTimeout(() => {
        const el = diffScrollRef.current;
        el?.querySelectorAll<HTMLElement>(
          '[data-diff-anchor].diff-anchor-new',
        ).forEach(a => {
          a.classList.remove('diff-anchor-new');
          a.style.removeProperty('--diff-stagger');
        });
      }, DIFF_ANIMATE_MS + maxDelay);
    };

    raf = requestAnimationFrame(run);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(cleanupTimer);
    };
  }, [isPreviewDiffMode, stagedPreviewMarkdown, stagedChangeSummaries]);

  /**
   * Saves default inputs to the API if they have changed.
   * Builds the payload by comparing current default keys with original keys,
   * sending current values for active defaults and null_value for removed ones.
   *
   * @returns Whether the save was successful (or skipped because nothing changed)
   */
  const saveDefaultInputsIfChanged = useCallback(async (): Promise<boolean> => {
    if (!automationId || !hasDefaultInputsChanged(automationId)) {
      return true; // Nothing changed, skip
    }

    const parsedId = parseResourceName(automation?.name || '').automation;
    if (!parsedId) return true;

    const storeData = getAutomationInputs(automationId);
    if (!storeData) return true;

    const currentKeys = storeData.defaultInputKeys;
    const currentValues = storeData.values;

    // Build the default_inputs payload
    const defaultInputsPayload: Record<string, Commonv1Value> = {};

    // Add current defaults with their values. A pinned input with nothing in
    // it is persisted as an explicit NULL_VALUE rather than skipped: the key
    // itself is what records the pin (the store rebuilds defaultInputKeys from
    // Object.keys(default_inputs) on load), so omitting it would silently drop
    // the pin as well as the value the next time the draft is opened.
    // `false` and `0` are real values and must survive this.
    for (const key of currentKeys) {
      const value = currentValues[key];
      // Commonv1Value's text field is `text`, not `string_value` — checking
      // the latter silently never matched, so this guard did nothing for the
      // text inputs it mainly exists for.
      const isBlank =
        !value ||
        value.null_value !== undefined ||
        value.text === '' ||
        (value.list !== undefined && (value.list.items ?? []).length === 0);
      defaultInputsPayload[key] = isBlank
        ? { null_value: 'NULL_VALUE' }
        : value;
    }

    try {
      await setDefaultInputsMutation.mutateAsync({
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: parsedId,
        data: {
          default_inputs: defaultInputsPayload,
          stage: isDraft
            ? V1AutomationStage.AUTOMATION_STAGE_DRAFT
            : V1AutomationStage.AUTOMATION_STAGE_PUBLISHED,
        },
      });
      // Sync the store so subsequent checks don't re-save
      syncOriginalDefaults(automationId);
      // Invalidate default inputs history so the UI reflects the latest entry
      queryClient.invalidateQueries({
        queryKey: getListDefaultInputsHistoryQueryKey(
          currentOrganizationId,
          currentWorkspaceId,
          parsedId,
        ),
      });
      return true;
    } catch (error) {
      errorHandler(error, () => {
        toast.error(t('messages.defaultInputsSaveError'));
        captureException(error as Error, {
          tags: {
            component: 'AutomationEditor',
            action: 'saveDefaultInputs',
          },
        });
      });
      return false;
    }
  }, [
    automationId,
    automation?.name,
    hasDefaultInputsChanged,
    getAutomationInputs,
    setDefaultInputsMutation,
    currentOrganizationId,
    currentWorkspaceId,
    isDraft,
    syncOriginalDefaults,
    queryClient,
    t,
  ]);

  const handleRun = useCallback(
    async (
      inputs: Record<string, Commonv1Value>,
      options?: { showValidationErrors?: boolean },
    ) => {
      if (invokeAutomationMutation.isPending) {
        return;
      }
      const showValidationErrors = options?.showValidationErrors ?? true;

      // Validate inputs if there are input specs
      const validateInputs = (): boolean => {
        if (!automation?.input_specs || automation.input_specs.length === 0) {
          return true; // No inputs required
        }

        const newErrors: Record<string, string> = {};

        automation.input_specs.forEach(spec => {
          const value = inputs[spec.name];
          const { isOptional } = unwrapType(spec.type);

          // Skip validation for optional fields
          if (isOptional) {
            return;
          }

          // Boolean inputs render as a checkbox, which always has a concrete
          // state. An untouched checkbox is a valid `false`, not a missing
          // value, so it should never be flagged as required.
          if (isBooleanType(spec.type)) {
            return;
          }

          // A `none_type` input has no value to provide — its row renders a
          // note, not a field — so it can never be "filled in" and must not be
          // flagged as missing. `buildRunInputs` sends Value::None for it.
          if (isNoneType(spec.type)) {
            return;
          }

          // Check if value is empty using the type-aware helper
          if (isValueEmpty(value)) {
            newErrors[spec.name] = tCommon('common.validation.fieldRequired', {
              fieldName: spec.name,
            });
          }
        });

        // Only surface validation errors after an explicit user submit.
        // Auto-run attempts in run mode should open the sheet without
        // pre-populating field errors.
        if (automationId && Object.keys(newErrors).length > 0) {
          setAutomationInputs(
            automationId,
            inputs,
            showValidationErrors ? newErrors : {},
          );
        }

        return Object.keys(newErrors).length === 0;
      };

      if (!validateInputs()) {
        setIsSaveAndRunModeTrue();
        // Open on the next tick so the sheet never renders one frame
        // in normal mode before switching into Save & Run mode.
        queueMicrotask(() => {
          setIsInputsOpen(true);
        });
        return;
      }

      // Get the automation name from the store (works for both created and fetched automations)
      const parsedAutomationId = parseResourceName(
        automation?.name || '',
      ).automation;

      if (!parsedAutomationId) {
        return;
      }

      // Fill in every declared input spec — an optional input the user left
      // alone must reach the runtime as Value::None, not be omitted.
      const processedInputs = buildRunInputs(automation?.input_specs, inputs);

      try {
        // Save default inputs before running if they have changed
        const defaultsSaved = await saveDefaultInputsIfChanged();
        if (!defaultsSaved) {
          return; // Don't proceed if default inputs save failed
        }

        setIsInputsOpen(false);
        setIsSaveAndRunModeFalse();
        const response = await invokeAutomationMutation.mutateAsync(
          {
            automation: parsedAutomationId,
            organization: currentOrganizationId,
            workspace: currentWorkspaceId,
            data: {
              inputs: processedInputs,
              enable_astral_on_rain: isAstralV2Enabled && isQuillV2Automation,
              stage: isDraft
                ? V1AutomationStage.AUTOMATION_STAGE_DRAFT
                : V1AutomationStage.AUTOMATION_STAGE_PUBLISHED,
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({
                queryKey: getListRunsQueryKey(
                  currentOrganizationId,
                  currentWorkspaceId,
                  automationId,
                ),
              });
            },
          },
        );

        // The response should contain a run ID for tracking execution
        if (response.data?.run_id) {
          const { run: runId } = parseResourceName(response.data.run_id);
          if (!isDraft) {
            setAutomationInputs(automationId, {}, {});
          }

          if (automationId && runId) {
            // Invalidate all automation-related caches when in run mode
            if (isRunMode) {
              const queryKeysToInvalidate = [
                getListAutomationsQueryKey(
                  currentOrganizationId,
                  currentWorkspaceId,
                ),
                getListAutomationSummariesQueryKey(
                  currentOrganizationId,
                  currentWorkspaceId,
                ),
                getListAutomationsInfiniteQueryKey(
                  currentOrganizationId,
                  currentWorkspaceId,
                ),
                getListAutomationSummariesInfiniteQueryKey(
                  currentOrganizationId,
                  currentWorkspaceId,
                ),
                getQueryAutomationRunAggregatesQueryKey(
                  currentOrganizationId,
                  currentWorkspaceId,
                ),
                getQueryDailyRunAggregatesQueryKey(
                  currentOrganizationId,
                  currentWorkspaceId,
                ),
                getListExceptionsQueryKey(
                  currentOrganizationId,
                  currentWorkspaceId,
                ),
              ];

              queryKeysToInvalidate.forEach(queryKey => {
                queryClient.invalidateQueries({ queryKey });
              });
            }

            // Navigate to appropriate run page based on isDraft
            if (isDraft) {
              navigate(
                getRoutePath('DRAFT_RUN', {
                  orgId: currentOrganizationId,
                  workspaceId: currentWorkspaceId,
                  draftId: automationId,
                  runId,
                }),
                {
                  // In RunMode, we should replace, so when user clicks on back,
                  // we won't end up re-running the draft again
                  replace: isRunMode,
                  // Started from the map, so the run opens on its map too.
                  state: showAopMapView ? AOP_MAP_LOCATION_STATE : undefined,
                },
              );
            } else {
              navigate(
                getRoutePath('AUTOMATION_RUN', {
                  orgId: currentOrganizationId,
                  workspaceId: currentWorkspaceId,
                  automationId: automationId,
                  runId,
                }),
                {
                  // In RunMode, we should replace, so when user clicks on back,
                  // we won't end up re-running the automation again
                  replace: isRunMode,
                  state: showAopMapView ? AOP_MAP_LOCATION_STATE : undefined,
                },
              );
            }
          }
        }
      } catch (error) {
        // Check for insufficient credits error first
        if (checkIsInsufficientCreditsError(error)) {
          toast.error(
            tAutomation('automation.invoke.insufficientCredits.message'),
          );
          captureException(error as Error, {
            tags: {
              component: 'AutomationEditor',
              action: 'handleRun',
              errorType: 'insufficient_credits',
              isRunMode: isRunMode ? 'true' : 'false',
              isDraft: isDraft ? 'true' : 'false',
            },
          });
        } else {
          errorHandler(error, () => {
            toast.error(tAutomation('automation.invoke.error'));
            captureException(error as Error, {
              tags: {
                component: 'AutomationEditor',
                action: 'handleRun',
                isRunMode: isRunMode ? 'true' : 'false',
                isDraft: isDraft ? 'true' : 'false',
              },
            });
          });
        }
      }
    },
    [
      automation?.name,
      automation?.input_specs,
      automationId,
      tCommon,
      setAutomationInputs,
      setIsSaveAndRunModeTrue,
      setIsSaveAndRunModeFalse,
      invokeAutomationMutation,
      currentOrganizationId,
      currentWorkspaceId,
      isDraft,
      queryClient,
      isRunMode,
      navigate,
      tAutomation,
      saveDefaultInputsIfChanged,
      showAopMapView,
    ],
  );
  const debouncedHandleRun = useDebounceCallback(
    handleRun,
    UI_ACTION_DEBOUNCE_MS,
    { leading: true, trailing: false },
  );

  // Fires `test_automation` via the mounted AutomationEditorChat's sendTestRun
  // instead of a real run — same inputs sheet as Run, different sink.
  //
  // The inputs go through the same `buildRunInputs` fill as a real run: the
  // sheet's values only contain fields the user actually touched, so without it
  // an untouched optional input is absent from the tool call rather than an
  // explicit Value::None, and an untouched boolean loses its implicit `false`.
  const handleTest = useCallback(
    async (inputs: Record<string, Commonv1Value>) => {
      // Loud rather than silent, so a gating regression can't read as a dead button.
      if (!sendTestRunRef.current) {
        setIsTestInputsOpen(false);
        toast.error(tAutomation('automation.invoke.error'));
        captureException(new Error('Test run requested with no chat mounted'), {
          tags: { component: 'AutomationEditor', action: 'handleTest' },
        });
        return;
      }
      setIsTestRunning(true);
      setFocusedTestRun(undefined);
      setPendingTestStartedAtMs(Date.now());
      setDocumentTab('document');
      try {
        setIsTestInputsOpen(false);
        await sendTestRunRef.current(
          buildRunInputs(automation?.input_specs, inputs),
        );
      } catch (error) {
        setPendingTestStartedAtMs(undefined);
        errorHandler(error, () => {
          toast.error(tAutomation('automation.invoke.error'));
        });
      } finally {
        setIsTestRunning(false);
      }
    },
    [automation?.input_specs, tAutomation],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: Prefill the inputs store from "Run again" router state; unmounting clears non-default inputs, so this re-applies on every mount
  useEffect(() => {
    const previousRunInputs = runAgainInputsFrom(location.state);
    if (!isRunMode || !automationId || !previousRunInputs) return;
    setAutomationInputs(
      automationId,
      { ...getAutomationInputs(automationId)?.values, ...previousRunInputs },
      {},
    );
  }, [
    isRunMode,
    automationId,
    location.state,
    getAutomationInputs,
    setAutomationInputs,
  ]);

  // Auto-run logic: If in automation run mode, automatically attempt to run
  // handleRun will validate inputs and either run or show input sheet
  // eslint-disable-next-line no-restricted-syntax -- reason: Auto-run automation when in run mode
  useEffect(() => {
    if (
      !isRunMode ||
      !automation ||
      isLoadingExistingAutomation ||
      hasAutoRunAttempted.current ||
      invokeAutomationMutation.isPending
    ) {
      return;
    }

    hasAutoRunAttempted.current = true;
    // Read current input values from store at the time of execution
    const currentInputs = automationId
      ? getAutomationInputs(automationId)?.values || {}
      : {};
    // A repeated run's inputs are for the user to review, not to run unseen.
    if (runAgainInputsFrom(location.state)) {
      setIsSaveAndRunModeTrue();
      queueMicrotask(() => setIsInputsOpen(true));
      return;
    }
    handleRun(currentInputs, { showValidationErrors: false });
  }, [
    isRunMode,
    automation,
    isLoadingExistingAutomation,
    invokeAutomationMutation.isPending,
    handleRun,
    automationId,
    getAutomationInputs,
    location.state,
    setIsSaveAndRunModeTrue,
  ]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Reset automation store when component unmounts to prevent stale state
  useEffect(() => {
    return () => {
      // Get the threadId BEFORE clearing the store (clearQuillChat removes it)
      const threadId = getThreadId(automationId);
      resetAutomation();
      clearNonDefaultInputs();
      clearQuillChat(automationId);
      setLoading(true);
      // Remove thread messages infinite query cache to prevent showing stale messages on next mount
      // Use the actual threadId (not automationId) to match the query key used by useListThreadMessagesInfinite
      if (threadId) {
        queryClient.removeQueries({
          queryKey: getListThreadMessagesInfiniteQueryKey(
            currentOrganizationId,
            currentWorkspaceId,
            automationId,
            QUILL_AGENT_NAME,
            threadId,
          ),
        });
      }
    };

    // We only want to reset the automation store when the component unmounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationId]);

  const handleInputValuesChange = (
    newValues: Record<string, Commonv1Value>,
  ) => {
    if (!automationId) return;

    // Clear errors for fields that have been changed
    const changedFields = Object.keys(newValues);
    const newErrors = { ...inputErrors };
    let hasErrorChanges = false;

    changedFields.forEach(field => {
      if (newErrors[field]) {
        delete newErrors[field];
        hasErrorChanges = true;
      }
    });

    // Update store with new values and cleared errors
    setAutomationInputs(
      automationId,
      newValues,
      hasErrorChanges ? newErrors : inputErrors,
    );
  };

  const handleSetInputsOpen = (open: boolean) => {
    setIsInputsOpen(open);
    if (!open) {
      setIsSaveAndRunModeFalse();
    }
  };

  /**
   * Handles toggling an input's default status
   *
   * @param inputName - The name of the input to toggle
   */
  const handleToggleDefault = (inputName: string) => {
    if (!automationId) return;
    toggleDefaultInput(automationId, inputName);
  };

  /**
   * Handles errors change from the inputs sheet
   * Updates the store with new errors (typically empty to clear)
   */
  const handleInputErrorsChange = (newErrors: Record<string, string>) => {
    if (!automationId) return;
    setAutomationInputs(automationId, inputValues, newErrors);
  };

  const handleSaveInputs = async () => {
    if (!automationId) return;

    // Save default inputs before closing if they have changed
    await saveDefaultInputsIfChanged();

    setIsInputsOpen(false);
    setIsTestInputsOpen(false);

    // Inputs are already saved in store via handleInputValuesChange
    // This just shows a success message
    toast.success(tAutomation('automation.message.inputsSaved'));
  };

  // Drives the manifest-upgrade banner in BOTH branches below: read-only
  // (the published automation's Document view has no open chat thread to
  // send into directly) and draft-with-open-chat (which does have one, but
  // the upgrade prompt always gets its own fresh thread rather than landing
  // mid-way through whatever conversation is currently active — hence
  // `onThreadCreated` here instead of the direct-send-into-rainThreadId this
  // used to do). Read-only has nothing to switch to in place, so it omits
  // `onThreadCreated` and gets the hook's default navigate-to-draft-editor.
  const {
    upgrade: upgradeManifest,
    isUpgrading: isUpgradingManifest,
    hasRequested: hasRequestedManifestUpgrade,
  } = useUpgradeAutomationManifest(automationId, {
    // The live version hands the thread to its mounted chat, then switches to
    // the draft the upgrade forked; other read-only views navigate there.
    onThreadCreated: isPublishedDocument
      ? threadId => {
          setSelectedRainThreadId(threadId);
          void queryClient.invalidateQueries({ queryKey: liveDraftQueryKey });
          navigate(
            getRoutePath('DRAFT_EDITOR', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
              draftId: automationId,
            }),
          );
        }
      : isReadOnly
        ? undefined
        : setSelectedRainThreadId,
  });
  // The preview-review kickoff (quill opening an empty preview thread with the
  // gap-closing questions) lives in AutomationEditorChat, gated on `isPreview` below —
  // there the message count is known, so it fires for a pre-existing empty
  // thread too, not only a freshly created one.

  // Editor selection precedence: version-history diff > staged preview diff >
  // normal (editable preview / read-only AOP) view.
  const editor = isDiffMode
    ? diffEditor
    : isPreviewDiffMode
      ? previewDiffEditor
      : defaultEditor;

  // Shared, cross-component store for "Select"-ed preview snippets and
  // step-added context chips, scoped by rainThreadId. The chat composer
  // (RainChatView) reads the same slot and renders each snippet as a
  // removable context chip, folding them into the next message on send —
  // the "add selection to chat" pattern. Rain-only: the manifest that gates
  // ManifestStepCard's "add to chat" button only ever exists for a Quill v2
  // (Rain) automation, so there's no legacy-chat case to scope for here.
  const {
    snippets: selectionSnippets,
    addSnippet: addSelectionSnippet,
    removeSnippet: removeSelectionSnippet,
  } = useSelectionContext(rainThreadId);

  // Step number -> snippet id, for steps currently referenced in the chat
  // composer. Backs ManifestStepCard's toggle button: a second click on an
  // already-referenced step removes it rather than adding a duplicate chip.
  const referencedStepSnippetIds = useMemo(() => {
    const byStepNumber = new Map<number, string>();
    for (const snippet of selectionSnippets) {
      const reference = parseStepReference(snippet.text);
      if (reference) byStepNumber.set(reference.number, snippet.id);
    }
    return byStepNumber;
  }, [selectionSnippets]);

  const isStepReferencedInChat = useCallback(
    (stepNumber: number) => referencedStepSnippetIds.has(stepNumber),
    [referencedStepSnippetIds],
  );

  // ManifestStepCard's "add to chat" toggle button; hidden entirely when no
  // chat is visible. Tags the reference with the focused run's id when a
  // live run preview is open, so the agent knows which run's execution the
  // user means, not just the step's static definition.
  const toggleStepInChat = useCallback(
    (step: V1Step) => {
      const stepNumber = step.number ?? -1;
      const existingSnippetId = referencedStepSnippetIds.get(stepNumber);
      if (existingSnippetId) {
        removeSelectionSnippet(existingSnippetId);
      } else {
        addSelectionSnippet(formatStepContext(step, focusedTestRun?.runId));
      }
    },
    [
      referencedStepSnippetIds,
      removeSelectionSnippet,
      addSelectionSnippet,
      focusedTestRun?.runId,
    ],
  );
  // A live legacy automation shows no chat to add a step to.
  const handleToggleStepInChat =
    isChatVisible && (!isPublishedDocument || isQuillV2Automation)
      ? toggleStepInChat
      : undefined;

  /** Row selection only controls the document/rail treatment. */
  const handleSelectDraftStep = useCallback((step: V1Step) => {
    const stepNumber = step.number;
    if (stepNumber === undefined) return;

    setSelectedDraftStepNumber(current =>
      current === stepNumber ? undefined : stepNumber,
    );
  }, []);

  const [, setIsDraftRailCollapsed] = useDraftRailCollapsed();
  const [, setIsConfigRailExpanded] = useConfigRailExpanded();
  const handleOpenDraftStep = useCallback(
    (step: V1Step) => {
      if (step.number === undefined) return;
      setSelectedDraftStepNumber(step.number);
      if (isAssetWorkspace) setIsConfigRailExpanded(true);
      else setIsDraftRailCollapsed(false);
    },
    [isAssetWorkspace, setIsConfigRailExpanded, setIsDraftRailCollapsed],
  );

  const handleClearSelectedDraftStep = () => {
    setSelectedDraftStepNumber(undefined);
  };

  const handleOpenDraftVersionHistory = () => {
    handleClearSelectedDraftStep();
    openVersionHistory();
  };

  /**
   * Adds the current TipTap selection to the Rain chat composer as a cancelable
   * context chip (Cursor "select -> add to chat" pattern), so the user can type
   * an instruction alongside it and have quill apply it via artifact_edit +
   * save_automation.
   */
  const handleQuoteSelectionToChat = useCallback(() => {
    // Use whichever editor is on screen — the editable preview (defaultEditor)
    // or the read-only staged-diff (previewDiffEditor) — so a user can select
    // proposed text mid-review and ask quill to change it further.
    if (!editor || !rainThreadId) return;
    const { selection } = editor.state;
    const { from, to } = selection;
    if (from === to) return;

    // A table selection is a ProseMirror CellSelection, not a text range —
    // textBetween(from, to) would only capture the head cell. Pull each
    // selected cell's text instead and join it into a readable row/range.
    let selectedText: string;
    if (selection instanceof CellSelection) {
      const cellTexts: string[] = [];
      selection.forEachCell(cell => {
        const text = cell.textContent.trim();
        if (text) cellTexts.push(text);
      });
      selectedText = cellTexts.join(' | ');
    } else {
      selectedText = editor.state.doc.textBetween(from, to, '\n').trim();
    }
    if (!selectedText) return;

    // Anchor the chip to the text it came from, so both the blueprint and its
    // staged-diff review can keep a highlight over it while the chip exists.
    // Recorded in plain text rather than as positions, so it resolves against
    // either document — a selection made in the review still highlights back in
    // the blueprint once the change is accepted, and vice versa.
    const anchor = createTextAnchor(editor.state.doc, from, to) ?? undefined;

    addSelectionSnippet(selectedText, anchor);

    // Collapse the selection so the "Select" bubble menu (shown only while text
    // is selected: shouldShow={from !== to}) dismisses itself — otherwise it
    // lingers over the text the user just added as a chip.
    editor.commands.setTextSelection(to);

    // Focus the chat composer so the user can start typing their instruction
    // immediately, mirroring NewDraftChat's template-chip focus behavior.
    requestAnimationFrame(() => {
      const textarea = rootRef.current?.querySelector<HTMLTextAreaElement>(
        '[data-slot="chat-input-textarea"]',
      );
      textarea?.focus();
    });
  }, [editor, rainThreadId, addSelectionSnippet]);

  // The highlight for each anchored chip. Derived from the same snippet list
  // the composer renders its chips from, so the two can't drift: a chip the
  // user removed, or the whole set cleared by sending the message, drops out of
  // here on the same render. Step-reference chips carry no anchor (they point
  // at a step card, not a range) and are skipped.
  const selectionHighlightAnchors = useMemo<ISelectionHighlightAnchor[]>(
    () =>
      selectionSnippets.flatMap(snippet =>
        snippet.anchor ? [{ id: snippet.id, ...snippet.anchor }] : [],
      ),
    [selectionSnippets],
  );

  // Both documents that can be on screen while the chat is open get the same
  // highlights: the blueprint itself, and the staged-diff review of quill's
  // proposed change. Each resolves the anchors against its own text, so the
  // review shows the selection wherever that text ended up in the diff (and
  // shows none if the change removed it).
  // eslint-disable-next-line no-restricted-syntax -- reason: Imperative TipTap API call to sync decorations with the chip list
  useEffect(() => {
    defaultEditor?.commands.setSelectionHighlights(selectionHighlightAnchors);
    previewDiffEditor?.commands.setSelectionHighlights(
      selectionHighlightAnchors,
    );
  }, [defaultEditor, previewDiffEditor, selectionHighlightAnchors]);

  /**
   * Sends the "Make deterministic" trigger message to the quill2 thread. The
   * chat is already mounted (it renders alongside the preview), so the
   * server's input echo over SSE renders it — no optimistic seed needed.
   */
  const handleMakeDeterministic = useCallback(async () => {
    if (!rainThreadId || !automationId) return;
    setIsSendingMakeDeterministic(true);
    try {
      await sendRainMessageAsync({
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: automationId,
        agentId: RAIN_QUILL_AGENT_ID,
        threadId: rainThreadId,
        data: { message: buildRainUserMessage(MAKE_DETERMINISTIC_MESSAGE) },
      });
      // Refetch the log so it carries the request's echo — that's what holds
      // the building phase from here on, including across a reload.
      await queryClient.invalidateQueries({
        queryKey: getAutomationGetEventsQueryKey(
          currentOrganizationId,
          currentWorkspaceId,
          automationId,
          RAIN_QUILL_AGENT_ID,
          rainThreadId,
          { compacted: false },
        ),
      });
    } catch (error) {
      errorHandler(error, () => {
        toast.error(tAutomation('automation.preview.error.makeDeterministic'));
        captureException(error as Error, {
          tags: { component: 'AutomationEditor', action: 'makeDeterministic' },
        });
      });
    } finally {
      setIsSendingMakeDeterministic(false);
    }
  }, [
    automationId,
    currentOrganizationId,
    currentWorkspaceId,
    queryClient,
    rainThreadId,
    sendRainMessageAsync,
    tAutomation,
  ]);

  const handleAddIntegrationToChat = useAddIntegrationToChat({
    organizationId: currentOrganizationId,
    workspaceId: currentWorkspaceId,
    automationId,
    threadId: rainThreadId,
  });
  const canAddIntegrationFromRail =
    isQuillV2Automation && isChatVisible && Boolean(rainThreadId);

  /**
   * Quill staged an edit to `preview.md` this turn (artifact_edit/write, not a
   * commit). Fetch the thread's staged view of the artifact.
   *
   * Two cases:
   * - There's already a COMMITTED preview (isPreviewAutomation) — this is a
   *   chat edit to something the user is already looking at, so hold the
   *   staged text as a reviewable diff (Accept/Discard) against it.
   * - There's nothing committed yet (a brand-new draft's very first
   *   artifact_write) — there's no prior version to diff against, so commit
   *   it immediately instead of surfacing a review step. Without this, the
   *   first draft stays staged in the thread forever and the editor pane has
   *   nothing committed to render, even though quill already "wrote" it.
   *
   * Gated on isUnbuiltQuillV2 (not isPreviewAutomation) so it fires in both
   * cases above. Reads the raw function (not the cached hook) so a fresh turn
   * always sees the latest stage.
   */
  const handleQuillArtifactStaged = useCallback(async () => {
    if (!rainThreadId || !automationId || !isUnbuiltQuillV2) return;
    setIsResolvingPreviewDiff(true);
    try {
      const response = await automationGetArtifactContent(
        currentOrganizationId,
        currentWorkspaceId,
        automationId,
        RAIN_QUILL_AGENT_ID,
        rainThreadId,
        BLUEPRINT_ARTIFACT_URI_ENCODED,
      );
      const base64 = response.data?.data;
      const staged = base64 ? decodeBase64ToText(base64) : '';

      if (staged && !previewMarkdown) {
        await savePreviewMarkdown(staged);
        return;
      }

      // Only surface a diff when the staged text actually diverges from the
      // committed preview — otherwise there's nothing to review.
      setStagedPreviewMarkdown(
        staged && staged !== (previewMarkdown ?? '') ? staged : null,
      );
    } catch (error) {
      captureException(error as Error, {
        tags: {
          component: 'AutomationEditor',
          action: 'resolveStagedPreview',
        },
      });
    } finally {
      setIsResolvingPreviewDiff(false);
    }
  }, [
    automationId,
    currentOrganizationId,
    currentWorkspaceId,
    isUnbuiltQuillV2,
    previewMarkdown,
    rainThreadId,
    savePreviewMarkdown,
  ]);

  /**
   * Discards the thread's staged artifact changes so its working view snaps
   * back to the committed preview. Used by Discard (reject) so no rejected
   * staging lingers for quill's next read.
   */
  const discardStagedPreview = useCallback(async () => {
    if (!rainThreadId || !automationId) return;
    await discardArtifactsAsync({
      organization: currentOrganizationId,
      workspace: currentWorkspaceId,
      automation: automationId,
      agentId: RAIN_QUILL_AGENT_ID,
      threadId: rainThreadId,
      data: {},
    });
  }, [
    automationId,
    currentOrganizationId,
    currentWorkspaceId,
    discardArtifactsAsync,
    rainThreadId,
  ]);

  /** Best-effort note to the chat so quill knows how the review landed. */
  const notifyQuill = useCallback(
    async (message: string) => {
      if (!rainThreadId || !automationId) return;
      await sendRainMessageAsync({
        organization: currentOrganizationId,
        workspace: currentWorkspaceId,
        automation: automationId,
        agentId: RAIN_QUILL_AGENT_ID,
        threadId: rainThreadId,
        data: { message: buildRainUserMessage(message) },
      }).catch(() => {
        // Best-effort — the preview state is already resolved either way.
      });
    },
    [
      automationId,
      currentOrganizationId,
      currentWorkspaceId,
      rainThreadId,
      sendRainMessageAsync,
    ],
  );

  /** Commits or discards the whole proposal, retaining it on failure for retry. */
  const finishPreviewReview = useCallback(
    async (commit: string | null) => {
      const isCommitting = commit !== null;
      setIsResolvingPreviewDiff(true);
      try {
        if (isCommitting) {
          // savePreviewMarkdown reports its own failures (toast + Sentry).
          if (!(await savePreviewMarkdown(commit))) return;
        } else {
          // Quill must not read the rejected proposal from its staging overlay.
          await discardStagedPreview();
        }
        if (!isCommitting) {
          await notifyQuill(
            tAutomation('automation.preview.discardAllMessage'),
          );
        }
        setStagedPreviewMarkdown(null);
      } catch (error) {
        errorHandler(error, () => {
          toast.error(
            tAutomation(
              isCommitting
                ? 'automation.preview.error.saveFailed'
                : 'automation.preview.error.discardFailed',
            ),
          );
          captureException(error as Error, {
            tags: {
              component: 'AutomationEditor',
              action: isCommitting
                ? 'commitPreviewReview'
                : 'discardPreviewReview',
            },
          });
        });
      } finally {
        setIsResolvingPreviewDiff(false);
      }
    },
    [discardStagedPreview, notifyQuill, savePreviewMarkdown, tAutomation],
  );

  /** Saves the complete staged blueprint. */
  const handleAcceptPreviewDiff = useCallback(async () => {
    if (stagedPreviewMarkdown === null) return;
    await finishPreviewReview(stagedPreviewMarkdown);
  }, [finishPreviewReview, stagedPreviewMarkdown]);

  /** Drops the proposal without changing the committed blueprint. */
  const handleDiscardPreviewDiff = useCallback(async () => {
    await finishPreviewReview(null);
  }, [finishPreviewReview]);

  // Only the automation record gates the page now. `isResolvingRainThread` used
  // to gate it too, which meant the entire editor — nav bar included — waited
  // on a round trip that only the transcript needs. The chat renders without a
  // thread and fills it in, so there is nothing left here to wait for.
  // Gated on having nothing to SHOW, not on a request being in flight. Line
  // ~589 already falls back to the store when the query has not landed, and a
  // just-created draft is seeded there by NewDraftChat — so the frame can be
  // drawn from real values while the query catches up. Checking the query flag
  // alone meant a page we could fully render still spent a beat as a spinner.
  if (isLoadingExistingAutomation && !automation) {
    return (
      <Flex justify="center" align="center" width="full" height="full">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (isFetchError && fetchError) {
    if (fetchError.status === 403) {
      return (
        <Flex justify="center" align="center" width="full" height="full">
          <AccessDenied className="max-w-md" />
        </Flex>
      );
    }

    let title = tCommon('common.error.title');
    const icon: IIconProps['type'] = 'AlertCircle';
    let action: () => void = refetchAutomation;
    let actionLabel = tCommon('common.action.retry');
    let actionIcon: IIconProps['type'] = 'RefreshCw';
    let description = tCommon('common.error.subtitle');
    if (fetchError.status === 404) {
      title = tAutomation('automation.notFound.title');
      action = () => navigate(getRoutePath('HOME'));
      actionLabel = tCommon('common.action.goHome');
      actionIcon = 'Home';
      description = tAutomation('automation.notFound.description');
    }
    return (
      <Flex justify="center" align="center" width="full" height="full">
        <EmptyState
          icon={icon}
          title={title}
          description={description}
          actionLabel={actionLabel}
          actionIcon={actionIcon}
          onAction={action}
          className="w-full max-w-md"
        />
      </Flex>
    );
  }

  if (!automationId) {
    return null;
  }

  // The editor pane renders for a committed AOP (english_code, generated from
  // SPy) or for a not-yet-built preview (the preview.md artifact).
  const hasAOPCode =
    Boolean(automation?.english_code && automation.english_code.length > 0) ||
    hasPreviewContent;

  // The blueprint renders in the same document shell a ready draft gets: no
  // page header, a card with its own sticky title bar, and the blueprint's
  // CTAs carried by that bar. Version history (and the version diff it opens)
  // keeps the page header exactly as the draft does, so the shell stands down
  // there and the framed editor below takes over.
  const showBlueprintDocument =
    isPreviewAutomation && !isVersionHistoryOpen && !isDiffMode;

  // The bordered "card" frame shared by every content type in this pane
  // (markdown/diff editor, Steps view) — same background, border, rounding,
  // and shadow regardless of what's inside, so switching between them (or
  // focusing a test run) never changes the pane's outer shape.
  const editorFrameClassName = cn(
    'bg-card text-foreground/80',
    justBuiltVersion !== null
      ? // "Just built" banner sits on top and owns the rounded top edge +
        // divider, so the AOP rounds its bottom; together they form a fully
        // rounded, bordered box that matches the normal built card below.
        'border border-t-0 rounded-b-xl shadow-custom'
      : 'border rounded-xl shadow-custom',
  );

  const editorContentClassName = cn(
    editorFrameClassName,
    'w-full h-full overflow-y-auto [&_.ProseMirror]:h-auto [&_.ProseMirror]:pb-16',
  );

  // Inside the blueprint document card the card owns the frame, the 40px page
  // padding and the scrolling, so the editor keeps only its bottom buffer and
  // cancels the padding ProseMirror bakes into its own node.
  const blueprintEditorClassName =
    'w-full text-foreground/80 [&_.ProseMirror]:h-auto [&_.ProseMirror]:p-0 [&_.ProseMirror]:pb-16';

  /**
   * Generates a unique key based on panel configuration to force remount when toggling.
   */
  const getPanelGroupKey = () => {
    if (isVersionHistoryOpen) return 'editor-history';
    // The map takes the whole split, so it needs its own key (and its own
    // `autoSaveId`) rather than inheriting the chat-editor sizes.
    if (showAopMapView && !isAssetLayout) return 'editor-map';
    if (showDraftRightRail) return 'chat-editor-draft-rail';
    if (showEditor) return 'chat-editor';
    return 'chat-only';
  };

  /**
   * Handles closing the version history panel and clearing the selected version.
   */
  const handleCloseVersionHistory = () => {
    closeVersionHistory();
    clearVersionSelection();
    setAutoDiffOnHistoryOpen(false);
  };

  const showEditor = hasAOPCode;
  // The live version keeps the draft's rail: triggers, integrations and
  // tweaks belong to the automation, not to one version.
  const showDraftRightRail = Boolean(
    (isDraft ? !isReadOnly : isPublishedDocument) &&
      !isRunMode &&
      !isVersionHistoryOpen &&
      !isDiffMode &&
      // The map takes the whole stage — it is the reason the chat panel gives
      // up its half — so the rail stands down with it. Letting the two share
      // the stage would also strand the reader: the back-to-drafts button
      // lives in the chat header, which the map unmounts, and the page header
      // that carries the other way back is suppressed while the rail is up.
      // The asset workspace keeps both, with the map as a document tab.
      (isAssetLayout || !showAopMapView) &&
      // A built draft needs a manifest the rail can actually read its steps,
      // library and config out of. A blueprint has no manifest at all — it
      // isn't built — but triggers, integrations, run history and publish all
      // hang off the automation rather than the manifest, and the blueprint
      // phase is exactly where a user sets a trigger or connects an
      // integration up front, so the rail shows there too.
      ((manifest && !isManifestStale(manifest)) || showBlueprintDocument),
  );
  // The rail carries the draft's own Run / Publish / history actions, and the
  // blueprint's document title bar carries its build CTA, so the page header
  // would only duplicate them on either.
  const canShowStandaloneBrowserRail = isChatPanelOpen && !showDraftRightRail;
  const showStandaloneBrowserRail =
    canShowStandaloneBrowserRail && hasBrowserSessions;
  // The asset workspace's own header names the automation, so the page header
  // only shows there for version history, in its compact form.
  const showAutomationPageHeader = isAssetLayout
    ? isVersionHistoryOpen
    : !showDraftRightRail && !showBlueprintDocument;
  const pagePaddingUnits =
    isDraft || isPublishedDocument
      ? DRAFT_PAGE_PADDING_UNITS
      : PAGE_PADDING_UNITS;
  // Panels share the root's width less its padding, the divider and the Browser rail.
  const panelFrameWidth = rootWidth
    ? rootWidth -
      pagePaddingUnits * SPACING_UNIT_PX * 2 -
      (showEditor ? CHAT_DIVIDER_WIDTH_PX : 0) -
      (showStandaloneBrowserRail ? BROWSER_RAIL_WITH_GAP_PX : 0) -
      (isAssetLayout && showDraftRightRail ? CONFIG_RAIL_WITH_GAP_PX : 0)
    : null;
  const panelGroupAutoSaveId = `automation-editor-${getPanelGroupKey()}`;
  const chatPanelSizes = getChatPanelSizes({
    frameWidth: panelFrameWidth,
    hasDocument: showEditor,
  });

  const handleChatDragging = (isDragging: boolean) => {
    const width = readPanelSize(chatPanelRef.current);
    if (isDragging) {
      chatWidthAtDragStart.current = width;
      return;
    }
    if (shouldRememberChatWidth(chatWidthAtDragStart.current, width)) {
      rememberChosenChatWidth(panelGroupAutoSaveId);
    }
    chatWidthAtDragStart.current = null;
  };

  const handleResetChatWidth = () => {
    forgetChosenChatWidth(panelGroupAutoSaveId);
    chatPanelRef.current?.resize(chatPanelSizes.defaultSize);
  };
  // The whole-automation workbench moves onto the document title, the same
  // slot `AutomationPageHeader` puts it in, so drafts keep the entry point
  // (and its `test-automation-workbench-button` anchor) once the rail shows.
  const canOpenDraftWorkbench = Boolean(
    showDraftRightRail &&
      automationId &&
      currentOrganizationId &&
      currentWorkspaceId,
  );
  // The stage follows the document: the live version tests published code.
  const handleOpenDraftWorkbench = () => {
    const path = getRoutePath('AUTOMATION_WORKBENCH', {
      orgId: currentOrganizationId,
      workspaceId: currentWorkspaceId,
      automationId,
    });
    navigate(`${path}?stage=${isPublishedDocument ? 'PUBLISHED' : 'DRAFT'}`);
  };
  // The step view is the only SOP view now, so the rail being up is the whole
  // condition — there is no markdown mode left to exclude.
  // Picking a step on the map opens the same rail beside it, the way the run
  // page's map does. Only while a step is picked: the map otherwise keeps the
  // whole stage, and the page header (with its way back) stays up. Held back
  // where the draft rail is (read-only, version history) for the same reasons.
  const [mapRailStepNumber] =
    mapSelectedSteps.size === 1 ? mapSelectedSteps : [];
  const mapPickedStep =
    showAopMapView &&
    !isReadOnly &&
    !isVersionHistoryOpen &&
    mapRailStepNumber !== undefined
      ? draftDisplayManifest?.steps?.find(
          step => step.number === mapRailStepNumber,
        )
      : undefined;
  // The asset workspace already has the config rail beside the map, so the
  // pick shows there, as a document pick does, instead of in a second rail.
  const isMapPickInConfigRail = isAssetWorkspace && showAopMapView;
  const mapRailStep = isMapPickInConfigRail ? undefined : mapPickedStep;

  const selectedDraftStep = !showDraftRightRail
    ? undefined
    : isMapPickInConfigRail
      ? mapPickedStep
      : draftDisplayManifest?.steps?.find(
          step => step.number === selectedDraftStepNumber,
        );
  // A selection only counts while the manifest on screen still carries that
  // step. A rebuild that renumbers or drops steps — or focusing a test run
  // whose manifest differs — would otherwise leave every row dimmed with
  // nothing highlighted and the rail back on its resource blocks.
  const selectedDraftStepNumberOnScreen = selectedDraftStep?.number;
  const draftChatBackButton =
    showDraftRightRail && !isAssetLayout ? (
      <TooltipActionButton
        tooltip={tAutomation('automation.header.tooltip.backToDrafts')}
        icon="ArrowLeft"
        variant="ghost"
        size="icon-sm"
        onClick={() =>
          navigate(
            getRoutePath('DRAFTS', {
              orgId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
            }),
          )
        }
        data-testid="draft-chat-back-button"
      />
    ) : undefined;

  // The blueprint's status line, in the slot the document title bar keeps for
  // the stage's meta. Lifted from the preview bar this layout replaced, so
  // every state keeps its own copy and test id; with none of them true the bar
  // falls back to the stage's own "Not built yet · Last saved at ..." meta.
  const blueprintStatus = (() => {
    if (!showBlueprintDocument) return undefined;
    if (isBuildingBlueprint) {
      return (
        <Flex
          align="center"
          gap={1.5}
          className="min-w-0"
          data-testid="preview-building-status"
        >
          <span
            aria-hidden
            className="size-1.5 shrink-0 animate-pulse rounded-full bg-primary"
          />
          <Text weight="medium" className="shrink-0 text-xs leading-4">
            {tAutomation('automation.preview.buildingTitle')}
          </Text>
          <Text color="muted" className="min-w-0 truncate text-xs leading-4">
            {tAutomation('automation.preview.buildingHint')}
          </Text>
        </Flex>
      );
    }
    if (endedBuildPhase && !isPreviewDiffMode) {
      return (
        <Flex
          align="center"
          gap={1.5}
          className="min-w-0"
          data-testid="preview-ended-status"
          data-ended={endedBuildPhase}
        >
          <Icon
            type="CircleStop"
            size="xs"
            className="shrink-0 text-muted-foreground"
          />
          <Text weight="medium" className="shrink-0 text-xs leading-4">
            {tAutomation(
              endedBuildPhase === 'stopped'
                ? 'automation.preview.stoppedTitle'
                : 'automation.preview.failedTitle',
            )}
          </Text>
          <Text color="muted" className="min-w-0 truncate text-xs leading-4">
            {tAutomation(
              endedBuildPhase === 'stopped'
                ? 'automation.preview.stoppedHint'
                : 'automation.preview.failedHint',
            )}
          </Text>
        </Flex>
      );
    }
    // Blueprint text is read-only since #2174, so there is no save to report;
    // the stage's own meta still ends in "Last saved at ...".
    return undefined;
  })();

  // The blueprint's build CTA. It sits in the rail's action row, where a built
  // draft's Run sits, so the build reads as this phase's primary action — the
  // rail's Publish / Test / Run cluster has nothing to act on until it lands.
  // Same handler, icons and disabled rules as the preview bar had, plus a
  // label that names the build while it runs.
  //
  // It stays mounted for the whole build, disabled with its loader running,
  // rather than unmounting once one starts: an empty slot drops the rail back
  // to Publish / Test / Run, which is the state this slot exists to prevent.
  // A disabled button still cannot start a second build on a live one.
  const isBuildInFlight = isSendingMakeDeterministic || isBuildingBlueprint;
  const blueprintBuild: IRailPrimaryAction | undefined = showBlueprintDocument
    ? {
        label: tAutomation(
          isBuildInFlight
            ? 'automation.preview.building'
            : endedBuildPhase
              ? 'automation.preview.buildAgain'
              : 'automation.preview.approveAndBuild',
        ),
        icon: endedBuildPhase ? 'RefreshCw' : 'Check',
        onClick: () => void handleMakeDeterministic(),
        // Disabled rather than hidden until the log has been read: hiding it
        // would flicker on every load, and the only thing that must not
        // happen in that window is starting a second build on a live one.
        // A pending staged review locks it too — the changes on screen are not
        // in the blueprint yet, so a build would leave them behind.
        disabled:
          !rainThreadId ||
          isBuildInFlight ||
          isPreviewDiffMode ||
          buildPhase === 'unknown',
        isLoading: isBuildInFlight,
        'data-testid': 'make-deterministic-button',
      }
    : undefined;

  // The same action as a button, for the title bar the rail-less phases use.
  const blueprintBuildAction = blueprintBuild ? (
    <Button
      size="sm"
      className="shrink-0"
      onClick={blueprintBuild.onClick}
      disabled={blueprintBuild.disabled}
      isLoading={blueprintBuild.isLoading}
      data-testid={blueprintBuild['data-testid']}
    >
      <Icon type={blueprintBuild.icon} size="sm" />
      {blueprintBuild.label}
    </Button>
  ) : undefined;

  const previewDiffActions = hasPendingPreviewProposal ? (
    <>
      <Button
        size="sm"
        onClick={() => void handleAcceptPreviewDiff()}
        isLoading={isResolvingPreviewDiff}
        disabled={isResolvingPreviewDiff}
        data-testid="accept-preview-diff-button"
      >
        {tAutomation('automation.preview.acceptChanges')}
        {stagedChangeSummaries.length > 0 && (
          <ToolbarBadge
            count={stagedChangeSummaries.length}
            data-testid="preview-changes-count"
          />
        )}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void handleDiscardPreviewDiff()}
        disabled={isResolvingPreviewDiff}
        data-testid="discard-preview-diff-button"
      >
        {tAutomation('automation.preview.discardChanges')}
      </Button>
    </>
  ) : undefined;

  // The asset workspace has no rail actions, so the build stays on the title bar.
  const blueprintTitleBarActions =
    showBlueprintDocument && isPreviewDiffMode
      ? previewDiffActions
      : showDraftRightRail && !isAssetLayout
        ? undefined
        : blueprintBuildAction;

  // The markdown/TipTap document: the editable blueprint, quill's staged
  // review of it, or a version diff. The body is the same wherever it lands;
  // only the frame differs — inside the blueprint card the card owns the
  // border, padding and scrolling, everywhere else the pane's own framed
  // editor does.
  const markdownDocument = (
    <div
      ref={diffScrollRef}
      className={cn(
        'relative w-full',
        // Inside the blueprint card the scroller owns the height, so the
        // document is as tall as its content; the framed editor is the
        // scroller itself and has to fill the pane.
        !showBlueprintDocument && 'h-full min-h-0',
      )}
    >
      {isPreviewAutomation && !isDiffMode && editor && rainThreadId && (
        <BubbleMenu
          editor={editor}
          shouldShow={({ from, to }) => from !== to}
          options={{ placement: 'top' }}
          className="flex items-center rounded-lg border bg-popover shadow-md"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={handleQuoteSelectionToChat}
            data-testid="quote-selection-to-chat-button"
          >
            <Icon type="Quote" size="sm" />
            {tAutomation('automation.preview.select')}
          </Button>
        </BubbleMenu>
      )}
      <EditorContent
        className={
          showBlueprintDocument
            ? blueprintEditorClassName
            : editorContentClassName
        }
        editor={editor}
      />
    </div>
  );

  // Everything the blueprint document card needs to name and act on the
  // automation. Shared by the loading and loaded bodies so the card, its
  // title bar and its CTAs never change shape between them.
  const blueprintDocumentProps = {
    automationId,
    title: automation?.display_name,
    lastSavedAt: automation?.update_time,
    isTitleEditable: isDraft && !isReadOnly,
    version: automation?.version,
    onOpenVersionHistory: handleOpenDraftVersionHistory,
    onExport: openExportDialog,
    status: blueprintStatus,
    actions: blueprintTitleBarActions,
  };

  // A built draft in the asset workspace: the shared document card owns the
  // heading, the run controls and the Steps / Map tabs.
  const isLiveAssetDocument =
    isPublishedDocument &&
    !isVersionHistoryOpen &&
    Boolean(manifest && !isManifestStale(manifest));
  const isAssetDocument =
    (isAssetWorkspace && showDraftRightRail && !showBlueprintDocument) ||
    isLiveAssetDocument;
  const documentStage = isPreviewAutomation ? 'blueprint' : 'draft';
  const isDeactivated =
    automation?.activation_state ===
    V1AutomationActivationState.AUTOMATION_ACTIVATION_STATE_DEACTIVATED;
  // Both versions exist: the document's badge switches between them.
  const liveVersion = isPublishedDocument
    ? automation?.version
    : automation?.latest_published_version;
  const switchDraftVersion = isPublishedDocument
    ? liveDraftVersion
    : automation?.version;
  const hasBothVersions =
    Boolean(liveVersion) &&
    (isPublishedDocument ? hasLiveDraft : documentStage === 'draft');
  const versionSwitch = hasBothVersions ? (
    <AutomationVersionSelect
      automationId={automationId}
      view={isPublishedDocument ? 'live' : 'draft'}
      liveVersion={liveVersion}
      draftVersion={
        switchDraftVersion !== liveVersion ? switchDraftVersion : undefined
      }
      isDeactivated={isDeactivated}
    />
  ) : undefined;
  const discardDraftOption = hasBothVersions
    ? {
        version: isPublishedDocument ? liveDraftVersion : automation?.version,
        // Not invalidated: a refetch that 404s keeps the discarded draft's
        // data. Reset where it's on screen; dropped before leaving the draft.
        onDiscarded: isPublishedDocument
          ? () => void queryClient.resetQueries({ queryKey: liveDraftQueryKey })
          : () => {
              queryClient.removeQueries({ queryKey: liveDraftQueryKey });
              // Replaced: the discarded draft's entry would lead nowhere.
              navigate(
                getRoutePath('AUTOMATION_AOP', {
                  orgId: currentOrganizationId,
                  workspaceId: currentWorkspaceId,
                  automationId,
                }),
                { replace: true },
              );
            },
      }
    : undefined;
  // A chat test runs the draft's code, so the live version offers only the
  // workbench.
  const canTestDraft =
    isQuillV2Automation &&
    isChatVisible &&
    !isPublishedDocument &&
    Boolean(rainThreadId);
  const runDraft = () =>
    debouncedHandleRun(inputValues, { showValidationErrors: false });
  const draftVersionHistoryLabel = automation?.version
    ? tAutomation('automation.header.tooltip.versionHistoryAt', {
        version: automation.version,
      })
    : tAutomation('automation.header.tooltip.versionHistory');
  const documentVersion = isPublishedDocument
    ? 'live'
    : documentStage === 'draft'
      ? 'draft'
      : undefined;
  const wrapInAssetDocument = (body: ReactNode) =>
    isAssetDocument ? (
      <AssetDocument
        title={
          <AutomationDocumentTitle
            automationId={automationId}
            title={automation?.display_name || manifest?.title}
            stage={isPublishedDocument ? 'published' : documentStage}
            stageSlot={
              versionSwitch ??
              (documentVersion && (
                <AssetVersionBadge
                  value={documentVersion}
                  isDeactivated={isDeactivated}
                  data-testid="automation-stage-badge"
                />
              ))
            }
            activationState={automation?.activation_state}
            isTitleEditable={!isReadOnly}
            version={automation?.version}
            onExport={openExportDialog}
            exportManifest={
              manifest && !isManifestStale(manifest) ? manifest : undefined
            }
            exportTitleFallback={automation?.display_name}
            showMenu={false}
            renameRequestCount={titleRenameRequests}
            titleClassName="max-w-[70%]"
          />
        }
        menu={
          <DocumentTitleMenu
            automationId={automationId}
            title={automation?.display_name || manifest?.title || ''}
            version={automation?.version}
            placement="overflow"
            isDraft={!isPublishedDocument}
            editAsDraft={
              isPublishedDocument
                ? {
                    hasDraft: hasLiveDraft,
                    isDeactivated,
                  }
                : undefined
            }
            discardDraft={discardDraftOption}
            onRename={
              isReadOnly
                ? undefined
                : () => setTitleRenameRequests(count => count + 1)
            }
            onExport={openExportDialog}
            exportManifest={
              manifest && !isManifestStale(manifest) ? manifest : undefined
            }
            exportTitleFallback={automation?.display_name}
          />
        }
        versionHistory={{
          label: draftVersionHistoryLabel,
          onClick: handleOpenDraftVersionHistory,
          'data-testid': 'document-version-button',
        }}
        tabs={{
          label: tCommon('common.assetWorkspace.document'),
          value: documentTab === 'test' ? 'test' : sopViewMode,
          onValueChange: value => {
            if (value === 'test') {
              setDocumentTab('test');
              return;
            }
            setDocumentTab('document');
            if (value !== sopViewMode) toggleSopViewMode();
          },
          items: [
            {
              value: 'steps',
              label: tAutomation('automation.workspace.tabs.document'),
              icon: 'FileText',
            },
            ...(canShowAopMapView
              ? [
                  {
                    value: 'map',
                    label: tAutomation('automation.workspace.tabs.map'),
                    icon: 'Workflow' as const,
                  },
                ]
              : []),
            {
              value: 'test',
              label: tAutomation('automation.workspace.tabs.test'),
              icon: 'FlaskConical',
            },
          ],
        }}
        meta={
          isPublishedDocument ? (
            <AutomationDocumentStatus
              stage="published"
              publishedAt={automation?.publish_time}
              running={documentRunning}
            />
          ) : (
            <AutomationDocumentStatus
              stage={documentStage}
              lastSavedAt={automation?.update_time}
              running={documentRunning}
              status={blueprintStatus}
            />
          )
        }
        data-testid="automation-asset-document"
      >
        {documentTab === 'test' ? (
          <AutomationTestPane
            version={isPublishedDocument ? 'live' : 'draft'}
            onTest={canTestDraft ? () => setIsTestInputsOpen(true) : undefined}
            canTest={(automation?.code_length ?? 0) > 0}
            isTesting={isTestInProgress}
            onOpenWorkbench={
              canOpenDraftWorkbench ? handleOpenDraftWorkbench : undefined
            }
          />
        ) : (
          body
        )}
      </AssetDocument>
    ) : (
      body
    );

  const definitionPanels = (
    <ResizablePanelGroup
      key={getPanelGroupKey()}
      direction="horizontal"
      className={cn(
        'min-h-0 min-w-0 flex-1',
        // Centers the chat and the Browser column as a pair.
        showStandaloneBrowserRail && !showEditor && 'max-w-3xl',
      )}
      autoSaveId={panelGroupAutoSaveId}
    >
      {isChatPanelOpen && (
        <>
          <ResizablePanel
            id="quill-chat-panel"
            order={1}
            ref={chatPanelRef}
            {...chatPanelSizes}
          >
            <div
              className={cn('h-full relative', {
                // Rain handles its own centering so the floating Todo card
                // can pin to the panel's right edge instead of the centered
                // content's right edge.
                'mx-auto max-w-3xl': !showEditor && !isQuillV2Automation,
              })}
            >
              {/* Rendered as soon as we know this is a Quill V2 automation,
                    WITHOUT waiting for its thread. Sending from the drafts hero
                    lands here immediately, and the thread is a round trip
                    behind that — gating on it meant the chat's whole frame
                    (header, selector, transcript, composer) appeared a second
                    or more after the page did. The chat is thread-optional
                    now: no stream opens and no events are fetched until the id
                    arrives, and the composer stays disabled for that window. */}
              {isQuillV2Automation && !hasRainThreadError ? (
                <AutomationEditorChat
                  key={automationId}
                  automationId={automationId}
                  rainThreadId={rainThreadId}
                  onSelectThread={setSelectedRainThreadId}
                  hideBackButton={isAssetLayout}
                  // The narrowed chat beside the Browser column has no room for a floating card.
                  todoVariant={
                    showEditor || showStandaloneBrowserRail
                      ? 'inline'
                      : 'floating'
                  }
                  showImportChecklist={isDraft}
                  // The same on both versions, so the composer holds still
                  // when switching between them.
                  className={isDraft || isAssetLayout ? 'pb-3' : undefined}
                  onArtifactStaged={handleQuillArtifactStaged}
                  isPreview={isPreviewAutomation}
                  isBuildingBlueprint={isBuildingBlueprint}
                  focusedTestRunToolCallId={focusedTestRun?.toolCallId}
                  focusedTestRunId={focusedTestRun?.runId}
                  onFocusTestRun={handleFocusTestRun}
                  onReady={handleEditorChatReady}
                  onBeforeSend={
                    isPublishedDocument ? handleLiveBeforeSend : undefined
                  }
                />
              ) : isQuillV2Automation && hasRainThreadError ? (
                <Flex
                  justify="center"
                  align="center"
                  width="full"
                  height="full"
                >
                  <EmptyState
                    icon="AlertCircle"
                    title={tCommon('common.error.title')}
                    description={tAutomation(
                      'automation.quillChat.error.createThread',
                    )}
                    actionLabel={tCommon('common.action.retry')}
                    actionIcon="RefreshCw"
                    onAction={retryResolveRainThread}
                    className="w-full max-w-md"
                  />
                </Flex>
              ) : (
                <QuillChat
                  key={automationId}
                  automationId={automationId}
                  initialMessage={initialMessage}
                  initialAttachments={initialAttachments}
                  showImportChecklist={isDraft}
                  className={isDraft ? 'pb-3' : undefined}
                  headerLeading={draftChatBackButton}
                />
              )}
            </div>
          </ResizablePanel>
          {showEditor && (
            <ResizableHandle
              variant="pill"
              aria-label={tAutomation('automation.editor.resizePanels')}
              onDragging={handleChatDragging}
              // The handle drops forwarded refs, so a lattice Tooltip cannot attach.
              title={tAutomation('automation.editor.resizePanels')}
              onDoubleClick={handleResetChatWidth}
              className="z-10"
            />
          )}
        </>
      )}
      {showEditor && (
        <>
          <ResizablePanel
            data-tour-id="editor-panel"
            order={2}
            defaultSize={
              isVersionHistoryOpen
                ? 70
                : isChatPanelOpen
                  ? showDraftRightRail && !isAssetLayout
                    ? 70
                    : 65
                  : 100
            }
            minSize={
              isVersionHistoryOpen
                ? 55
                : isChatPanelOpen
                  ? showDraftRightRail && !isAssetLayout
                    ? 64
                    : 55
                  : 100
            }
          >
            <Flex
              width="full"
              direction="row"
              flex="1"
              height="full"
              className={cn('relative', isDraft ? 'pb-4' : 'pb-7', {
                // Without the chat the document centres itself in a
                // readable measure — but the map wants every pixel, so it
                // opts out of that column.
                'mx-auto max-w-3xl':
                  !isChatPanelOpen && !isVersionHistoryOpen && !showAopMapView,
                'pl-2': isVersionHistoryOpen,
              })}
              data-testid="draft-editor-workspace"
            >
              <Flex
                direction="column"
                gap={
                  showBlueprintDocument || justBuiltVersion !== null
                    ? 0
                    : showDraftRightRail
                      ? 5
                      : 2
                }
                width="full"
                height="full"
                className={cn('min-w-0 flex-1', {
                  // Positioned wrapper for the dashed frame + comet overlays;
                  // the frame reads its rounded corners (all four) from here,
                  // so it has to match the document card's own radius.
                  'relative rounded-2xl shadow-custom': showBlueprintDocument,
                  // Keeps the ring on this box when a mid-build refetch drops
                  // the document for a beat.
                  'relative rounded-xl':
                    isBuildingBlueprint && !showBlueprintDocument,
                })}
              >
                {showBlueprintDocument && (
                  <DashedFrame
                    dashGap="8px"
                    thickness="2px"
                    // Above the document's own sticky title bar (z-30):
                    // the bar is opaque glass across the card's top edge,
                    // so a frame below it loses its top run of dashes and
                    // the blueprint reads as a plain card with a header.
                    className="z-40"
                  />
                )}
                <CometBorder
                  active={isBuildingBlueprint}
                  color="var(--color-primary)"
                  // Same reason as the frame, and above it so the beam
                  // travels over the dashes rather than under them.
                  className="z-50"
                  data-testid="blueprint-comet"
                  duration={1.5}
                />
                {justBuiltVersion !== null && !isFetchingAutomation && (
                  <Flex
                    align="center"
                    gap={2}
                    width="full"
                    paddingX={4}
                    paddingY={4}
                    className="mb-0 min-h-10 min-w-0 shrink-0 rounded-xl border bg-card shadow-custom"
                    data-testid="blueprint-built-banner"
                  >
                    <span
                      className="size-2 shrink-0 rounded-full bg-success"
                      aria-hidden="true"
                    />
                    <Text level="small" weight="semibold" className="shrink-0">
                      {tAutomation('automation.preview.builtVersion', {
                        version: justBuiltVersion,
                      })}
                    </Text>
                    <Text
                      level="small"
                      color="muted"
                      className="min-w-0 truncate"
                    >
                      {tCommon('common.justNow')}
                    </Text>
                  </Flex>
                )}
                {isDiffMode && !isEditorContentLoading && (
                  <Flex
                    align="center"
                    justify="between"
                    gap={2}
                    width="full"
                    className="shrink-0"
                    data-testid="diff-mode-bar"
                  >
                    <Badge variant="secondary" data-testid="diff-badge">
                      {selectedVersion?.version
                        ? tAutomation(
                            'automation.versionHistory.diff.changesInVersion',
                            { version: selectedVersion.version },
                          )
                        : tAutomation(
                            'automation.versionHistory.diff.changesIn',
                          )}
                    </Badge>
                    <Flex align="center" gap={4}>
                      <Flex align="center" gap={1.5}>
                        <span
                          aria-hidden
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: 'var(--success)' }}
                        />
                        <Text level="small" color="muted">
                          {tCommon('common.diff.added')}
                        </Text>
                      </Flex>
                      <Flex align="center" gap={1.5}>
                        <span
                          aria-hidden
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: 'var(--destructive)' }}
                        />
                        <Text level="small" color="muted">
                          {tCommon('common.diff.deleted')}
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                )}
                {!isDiffMode &&
                  !isPreviewDiffMode &&
                  !isEditorContentLoading &&
                  manifestNeedsUpgrade &&
                  (automation?.code_length ?? 0) > 0 &&
                  // Legacy Quill v1 automations never get a code_manifest
                  // at all — they don't go through jarvis. Upgrading only
                  // makes sense for a Quill v2 automation that simply
                  // hasn't been re-saved since the manifest field shipped.
                  // Sending this into a v1 automation's chat doesn't just
                  // silently no-op: it's been observed to destroy that
                  // automation's existing draft server-side.
                  isQuillV2Automation && (
                    <ManifestUpgradeBanner
                      onUpgrade={() => void upgradeManifest()}
                      isSending={isUpgradingManifest}
                      hasRequested={hasRequestedManifestUpgrade}
                    />
                  )}
                {/* Wrapped once, outside the branches, so the heading keeps its
                    place in the tree: its Publish refetches the automation on
                    mount, and a remount per branch would loop on the loading
                    state. */}
                {wrapInAssetDocument(
                  isEditorContentLoading &&
                    !(focusedTestRun && showManifestSopView) ? (
                    // The blueprint keeps its card and title bar while the
                    // content loads — dropping to a bare skeleton would flash
                    // the whole document frame away on every refetch.
                    showBlueprintDocument ? (
                      <BlueprintDocumentView {...blueprintDocumentProps}>
                        <div className="w-full p-12">
                          <AOPSkeleton />
                        </div>
                      </BlueprintDocumentView>
                    ) : isAssetDocument ? (
                      <div className="w-full p-12">
                        <AOPSkeleton />
                      </div>
                    ) : (
                      <div className={cn(editorContentClassName, 'p-12')}>
                        <AOPSkeleton />
                      </div>
                    )
                  ) : showManifestSopView ? (
                    <Flex
                      direction="column"
                      className={cn(
                        isDraft || isAssetDocument
                          ? 'bg-transparent text-foreground'
                          : editorFrameClassName,
                        'relative h-full min-h-0 w-full overflow-hidden',
                      )}
                    >
                      {(focusedTestRun || pendingTestStartedAtMs) && (
                        <TestRunBanner
                          // Remount per run so the banner's optimistic
                          // pause state can't follow a switch to another
                          // focused run.
                          key={focusedTestRun?.runId ?? 'starting'}
                          run={focusedTestRun ? focusedRunLive : undefined}
                          isStarting={!focusedTestRun}
                          startedAtMs={
                            focusedTestRun?.startedAtMs ??
                            pendingTestStartedAtMs
                          }
                          onClose={() => {
                            setPendingTestStartedAtMs(undefined);
                            setFocusedTestRun(undefined);
                          }}
                          runControlAction={
                            focusedTestRun ? focusedRunControlAction : null
                          }
                          isRunControlPending={isFocusedRunControlPending}
                          isRunControlPausing={isFocusedRunPausing}
                          isRunControlDisabled={isFocusedRunControlDisabled}
                          onRunControl={handleFocusedRunControl}
                        />
                      )}
                      {focusedTestRun &&
                        focusedRunManifestStatus === 'unavailable' && (
                          <Flex className="shrink-0 px-4 py-2">
                            <Text level="small" color="muted">
                              {tAutomation(
                                'automation.manifestSopView.testRun.unavailable',
                              )}
                            </Text>
                          </Flex>
                        )}
                      <Flex
                        direction="column"
                        className={cn(
                          // Y only. Scrolling X here drags the sticky section
                          // headers out of the columns they label; wide content
                          // carries its own overflow-x-auto instead.
                          'relative min-h-0 w-full min-w-0 flex-1 overflow-x-hidden overflow-y-auto',
                          (isDraft || isLiveAssetDocument) &&
                            '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                        )}
                      >
                        {isDraft || isLiveAssetDocument ? (
                          <DraftDocumentStepsView
                            manifest={draftDisplayManifest ?? {}}
                            runId={focusedTestRun?.runId}
                            titleFallback={automation?.display_name}
                            automationId={automationId}
                            updatedAt={automation?.update_time}
                            // The document owns the name only where the page
                            // header is hidden, and it may be renamed only
                            // where the old header allowed it. Read-only,
                            // version-history and run mode all keep the page
                            // header, so the name still renders exactly once.
                            showTitle={
                              !showAutomationPageHeader && !isAssetDocument
                            }
                            framed={!isAssetDocument}
                            // Nothing sits above the first section inside the
                            // asset document, nor in the workspace's history view.
                            padTop={
                              isAssetDocument ||
                              (isAssetLayout && showAutomationPageHeader)
                            }
                            isTitleEditable={isDraft && !isReadOnly}
                            // An unbuilt blueprint reaches this view through a
                            // focused test run's own manifest, and is still a
                            // blueprint while it does.
                            stage={
                              isPublishedDocument
                                ? 'published'
                                : isPreviewAutomation
                                  ? 'blueprint'
                                  : 'draft'
                            }
                            version={automation?.version}
                            onOpenVersionHistory={handleOpenDraftVersionHistory}
                            running={documentRunning}
                            // A blueprint reaches this view through a focused
                            // test run; its build status and CTA stay within
                            // reach there, as they were before the document
                            // owned the title bar.
                            status={blueprintStatus}
                            actions={blueprintTitleBarActions}
                            // Spec 1.3 removed the toolbar row these two used
                            // to live on; they belong to the title bar now.
                            onExport={openExportDialog}
                            // Markdown/SOP export, distinct from the zip above.
                            // The page header used to carry this and no longer
                            // renders once the rail is up, so without it the
                            // Markdown items vanish from a built draft.
                            exportManifest={
                              manifest && !isManifestStale(manifest)
                                ? manifest
                                : undefined
                            }
                            onOpenWorkbench={
                              canOpenDraftWorkbench
                                ? handleOpenDraftWorkbench
                                : undefined
                            }
                            onToggleMapView={
                              canShowAopMapView ? toggleSopViewMode : undefined
                            }
                            selectedStepNumber={selectedDraftStepNumberOnScreen}
                            /* The document still draws with the rail down
                                 (read-only, version history, diff), and then a
                                 row has nothing to open. Picking between two
                                 stable callbacks rather than wrapping one keeps
                                 the memoised rows off every editor render. */
                            onSelectStep={
                              showDraftRightRail ? handleSelectDraftStep : noop
                            }
                            onOpenStep={
                              showDraftRightRail ? handleOpenDraftStep : noop
                            }
                            onToggleStepInChat={handleToggleStepInChat}
                            isStepReferencedInChat={isStepReferencedInChat}
                            getStepStatus={
                              focusedTestRun
                                ? getFocusedRunStepStatus
                                : undefined
                            }
                            isRunActive={
                              !!focusedRunLive?.state &&
                              !isRunInTerminalState(focusedRunLive.state)
                            }
                            getStepIO={
                              focusedTestRun ? getFocusedRunStepIO : undefined
                            }
                            stepDetailsUnavailable={
                              !!focusedTestRun &&
                              focusedRunStepDetailsUnavailable
                            }
                            stepDetailsFailed={!!focusedRunEventsError}
                            getInputValue={
                              focusedTestRun
                                ? getFocusedRunInputValue
                                : undefined
                            }
                            getOutputValue={
                              focusedTestRun
                                ? getFocusedRunOutputValue
                                : undefined
                            }
                            inputValues={inputValues}
                            onInputValuesChange={handleInputValuesChange}
                            defaultInputKeys={defaultInputKeys}
                            onToggleDefault={handleToggleDefault}
                            inputsReadOnly={
                              invokeAutomationMutation.isPending ||
                              isTestRunning
                            }
                          />
                        ) : (
                          <ManifestSopView
                            title={automation?.display_name}
                            manifest={draftDisplayManifest ?? {}}
                            automationConfig={automationConfig}
                            onSaveConfigValue={saveConfigValue}
                            credentialsByAlias={credentialsByAlias}
                            onTestStep={handleOpenStepWorkbench}
                            onToggleStepInChat={handleToggleStepInChat}
                            isStepReferencedInChat={isStepReferencedInChat}
                            getStepStatus={
                              focusedTestRun
                                ? getFocusedRunStepStatus
                                : undefined
                            }
                            getStepIO={
                              focusedTestRun ? getFocusedRunStepIO : undefined
                            }
                            stepDetailsUnavailable={
                              !!focusedTestRun &&
                              focusedRunStepDetailsUnavailable
                            }
                            stepDetailsFailed={!!focusedRunEventsError}
                            getInputValue={
                              focusedTestRun
                                ? getFocusedRunInputValue
                                : undefined
                            }
                            getOutputValue={
                              focusedTestRun
                                ? getFocusedRunOutputValue
                                : undefined
                            }
                          />
                        )}
                      </Flex>
                    </Flex>
                  ) : showAopMapView ? (
                    // `editorFrameClassName` is the surface the Steps and
                    // Markdown views render on, so switching views changes
                    // the content and nothing around it.
                    <Flex
                      direction="column"
                      position="relative"
                      width="full"
                      height="full"
                      overflow="hidden"
                      className={cn(
                        !isAssetDocument && editorFrameClassName,
                        'min-h-0',
                      )}
                    >
                      {/* `width="full"`, not `Flex`'s default `w-auto`: this
                          is a column child of an `items-start` parent, so
                          without it the canvas shrinks to the graph's own
                          width and a short automation leaves the card's
                          surface showing beside it. */}
                      <Flex
                        direction="column"
                        flex="1"
                        width="full"
                        className="min-h-0"
                      >
                        <Suspense fallback={null}>
                          <AopFlowDiagram
                            // The focused run's own manifest when there is
                            // one: `focusedRunStepStatuses` is keyed on its
                            // step numbers, so the tiles have to be too.
                            manifest={draftDisplayManifest}
                            // Undefined outside a focused run, which leaves
                            // the map plain.
                            stepStatuses={
                              focusedTestRun
                                ? focusedRunStepStatuses
                                : undefined
                            }
                            connectionIcons={aopConnectionIcons}
                            selectedSteps={mapSelectedSteps}
                            onSelectedStepsChange={setMapSelectedSteps}
                          />
                        </Suspense>
                      </Flex>
                    </Flex>
                  ) : showBlueprintDocument ? (
                    <BlueprintDocumentView {...blueprintDocumentProps}>
                      {markdownDocument}
                    </BlueprintDocumentView>
                  ) : (
                    markdownDocument
                  ),
                )}
              </Flex>

              {/* Inputs/triggers describe a BUILT automation's input_specs;
                    an unbuilt SOP preview has none, so hide the widget until
                    the real AOP is shown. Also hide it while viewing a test
                    run — the run's own inputs are already shown on the
                    focused TestRunBanner/step view.
                    Two independent fixes here, for two different cases:
                    (1) `pointer-events-none` on the wrapper (+ `-auto` back
                    on just the button) — this `absolute`, full-width/height
                    box only visually centers the button, but its whole
                    bounding box still intercepts clicks by default even
                    where nothing is drawn, which was silently swallowing
                    clicks on whatever content happened to sit underneath it
                    (e.g. the Outputs table's "Show more" toggle) — a bug
                    regardless of whether a run is focused, whenever a short
                    automation's content ends up in that band.
                    (2) Skipping the wrapper entirely while a run is focused
                    (redundant with (1) for click-through, but also avoids
                    mounting/animating a widget that's permanently hidden
                    for the whole focused session). */}
              {!isEditorContentLoading &&
                !hasPreviewContent &&
                !focusedTestRun &&
                !showDraftRightRail && (
                  <Flex
                    align="center"
                    justify="center"
                    className="pointer-events-none absolute bottom-0 left-0 right-0 z-30 py-12"
                  >
                    <InputsTriggerButton
                      className="pointer-events-auto"
                      data-tour-id="inputs-trigger-button"
                      count={automation?.input_specs?.length || 0}
                      label={t('labels.inputs')}
                      onClick={() => setIsInputsOpen(true)}
                    />
                  </Flex>
                )}
              <AutomationInputsSheet
                isOpen={isInputsOpen || isTestInputsOpen}
                onOpenChange={open => {
                  if (!open) {
                    setIsTestInputsOpen(false);
                    handleSetInputsOpen(false);
                    return;
                  }
                  handleSetInputsOpen(open);
                }}
                inputSpecs={automation?.input_specs || []}
                values={inputValues}
                errors={inputErrors}
                onValuesChange={handleInputValuesChange}
                onErrorsChange={handleInputErrorsChange}
                onSaveInputs={handleSaveInputs}
                onRun={isTestInputsOpen ? handleTest : debouncedHandleRun}
                placement="center"
                isRunMode={isSaveAndRunMode}
                isTestMode={isTestInputsOpen}
                automationId={automationId || ''}
                automationName={automation?.display_name}
                isDraft={isDraft}
                defaultInputKeys={defaultInputKeys}
                onToggleDefault={handleToggleDefault}
              />
              {showDraftRightRail && !isAssetLayout && (
                <DraftEditorRightRail
                  automationId={automationId}
                  automationName={automation?.display_name}
                  manifest={draftDisplayManifest ?? EMPTY_MANIFEST}
                  inputSpecs={automation?.input_specs ?? []}
                  automationConfig={automationConfig}
                  onSaveConfigValue={saveConfigValue}
                  credentialsByAlias={credentialsByAlias}
                  selectedStep={selectedDraftStep}
                  selectedStepStatus={
                    selectedDraftStep?.number !== undefined
                      ? focusedRunStepStatuses.get(selectedDraftStep.number)
                      : undefined
                  }
                  selectedStepIO={
                    selectedDraftStep?.number !== undefined
                      ? focusedRunStepIO.get(selectedDraftStep.number)
                      : undefined
                  }
                  selectedStepRunningCalls={focusedRunRunningStepCalls}
                  // The rail's Triggers "+" opens the trigger picker now,
                  // not the inputs sheet — inputs keep their own entry point
                  // via the floating InputsTriggerButton below.
                  defaultInputKeys={defaultInputKeys}
                  onRun={() =>
                    debouncedHandleRun(inputValues, {
                      showValidationErrors: false,
                    })
                  }
                  onTest={
                    isQuillV2Automation &&
                    isChatVisible &&
                    Boolean(rainThreadId)
                      ? () => setIsTestInputsOpen(true)
                      : undefined
                  }
                  onTestStep={handleOpenStepWorkbench}
                  onAddIntegration={
                    canAddIntegrationFromRail
                      ? handleAddIntegrationToChat
                      : undefined
                  }
                  canRun={(automation?.code_length ?? 0) > 0}
                  canTest={(automation?.code_length ?? 0) > 0}
                  isRunning={invokeAutomationMutation.isPending}
                  isTesting={isTestInProgress}
                  primaryAction={blueprintBuild}
                />
              )}
              {mapRailStep && mapRailStepNumber !== undefined && (
                <DraftEditorRightRail
                  automationId={automationId}
                  automationName={automation?.display_name}
                  manifest={draftDisplayManifest ?? EMPTY_MANIFEST}
                  inputSpecs={automation?.input_specs ?? []}
                  automationConfig={automationConfig}
                  onSaveConfigValue={saveConfigValue}
                  credentialsByAlias={credentialsByAlias}
                  selectedStep={mapRailStep}
                  selectedStepStatus={
                    focusedTestRun
                      ? focusedRunStepStatuses.get(mapRailStepNumber)
                      : undefined
                  }
                  selectedStepIO={
                    focusedTestRun
                      ? focusedRunStepIO.get(mapRailStepNumber)
                      : undefined
                  }
                  selectedStepRunningCalls={focusedRunRunningStepCalls}
                  variant="map"
                />
              )}
            </Flex>
          </ResizablePanel>
          {isVersionHistoryOpen && (
            <ResizableHandle className="w-1 mt-8 bg-transparent hover:bg-primary/50 active:bg-primary/50" />
          )}
          {/* Only show the tour if the editor tour is done and the draft is still open */}
          {isDraft && isEditorTourDone && (
            <ManagedTourProvider tourConfig={draftAutomationEditViewTour}>
              <TourTrigger tourConfig={draftAutomationEditViewTour} />
            </ManagedTourProvider>
          )}
        </>
      )}

      {isVersionHistoryOpen && !isIDPPanelOpen && (
        <ResizablePanel
          id="version-history-panel"
          order={3}
          defaultSize={35}
          minSize={30}
          maxSize={45}
        >
          <Flex
            direction="column"
            height="full"
            paddingX={3}
            paddingY={4}
            className="mx-2.5"
          >
            <VersionHistoryPanel
              automationId={automationId}
              fromPublished={!isDraft}
              initialFilter={initialFilter}
              onVersionSelect={handleVersionHistorySelect}
              onFilterChange={setVersionHistoryFilter}
              onSelectionClear={clearVersionSelection}
              onRestoreSuccess={handleCloseVersionHistory}
              initialSelectionMode={
                autoDiffOnHistoryOpen ? 'previous' : 'latest'
              }
              changes={isDiffMode ? changeSummaries : undefined}
              onChangeActivate={handleChangeActivate}
              onChangeSelect={handleChangeSelect}
            />
          </Flex>
        </ResizablePanel>
      )}
    </ResizablePanelGroup>
  );

  const renderAutomationPageHeader = (
    renderWorkspaceHeader?: IAutomationPageHeaderProps['renderWorkspaceHeader'],
  ) => (
    <AutomationPageHeader
      showVersionHistory={!isRunMode && (!isDraft || hasAOPCode)}
      showRunHistory={isRunMode || (isDraft && hasAOPCode)}
      showIntegrations={!isDraft || hasAOPCode}
      showEditAsDraft={!isDraft && !isRunMode && isReadOnly}
      showPublish={isDraft && hasAOPCode}
      showRun
      isTitleEditable={isDraft && !isReadOnly}
      isReadOnly={isReadOnly}
      onRun={() =>
        debouncedHandleRun(inputValues, { showValidationErrors: false })
      }
      isRunning={invokeAutomationMutation.isPending}
      canRun={(automation?.code_length ?? 0) > 0}
      // Test fires through the mounted chat's `sendTestRun`, so it can only be
      // offered where that chat actually renders.
      // Test runs the draft's code, so the live version never offers it.
      showTest={
        isQuillV2Automation &&
        isChatVisible &&
        !isPublishedDocument &&
        Boolean(rainThreadId)
      }
      onTest={() => setIsTestInputsOpen(true)}
      isTesting={isTestInProgress}
      canTest={(automation?.code_length ?? 0) > 0}
      isVersionHistoryOpen={isVersionHistoryOpen}
      onVersionHistoryClick={openVersionHistory}
      onCloseVersionHistory={handleCloseVersionHistory}
      versionHistoryActions={previewDiffActions}
      selectedVersion={selectedVersion}
      isVersionHistoryCompact={isAssetLayout}
      renderWorkspaceHeader={renderWorkspaceHeader}
      automation={automation ?? undefined}
      // The editor toolbar is the crowded one, so its secondary actions
      // collapse into a three-dot menu left of Publish. Export lives there
      // rather than above the document, which the view toggle now occupies.
      overflowActions
      exportManifest={
        manifest && !isManifestStale(manifest) ? manifest : undefined
      }
      exportTitleFallback={automation?.display_name}
      // Drafts put this on the document's own title bar instead; this is
      // the slot for the paths that render no such bar — including the
      // map, which replaces that bar along with the document.
      onToggleMapView={
        canShowAopMapView && !showDraftRightRail ? toggleSopViewMode : undefined
      }
      isMapView={showAopMapView}
    />
  );

  const showBodyPageHeader =
    showAutomationPageHeader && (!isPublishedDocument || isVersionHistoryOpen);

  const editorContent = (
    <>
      {showBodyPageHeader && renderAutomationPageHeader()}
      <SupportAccessAlert
        className="px-2.5 pt-2.5"
        automationId={automationId}
      />
      {isAssetLayout ? (
        <AssetDefinitionLayout
          rail={
            showDraftRightRail ? (
              <AutomationConfigRail
                automationId={automationId}
                automationName={automation?.display_name}
                manifest={draftDisplayManifest ?? EMPTY_MANIFEST}
                inputSpecs={automation?.input_specs ?? []}
                automationConfig={automationConfig}
                onSaveConfigValue={saveConfigValue}
                credentialsByAlias={credentialsByAlias}
                selectedStep={selectedDraftStep}
                selectedStepStatus={
                  selectedDraftStep?.number !== undefined
                    ? focusedRunStepStatuses.get(selectedDraftStep.number)
                    : undefined
                }
                selectedStepIO={
                  selectedDraftStep?.number !== undefined
                    ? focusedRunStepIO.get(selectedDraftStep.number)
                    : undefined
                }
                selectedStepRunningCalls={focusedRunRunningStepCalls}
                defaultInputKeys={defaultInputKeys}
                onTestStep={handleOpenStepWorkbench}
                actions={
                  isAssetDocument
                    ? {
                        version: isPublishedDocument ? 'live' : 'draft',
                        onRun: runDraft,
                        canRun:
                          (automation?.code_length ?? 0) > 0 &&
                          !(isPublishedDocument && isDeactivated),
                        isRunning: invokeAutomationMutation.isPending,
                        isTesting: isTestInProgress,
                      }
                    : undefined
                }
                onAddIntegration={
                  canAddIntegrationFromRail
                    ? handleAddIntegrationToChat
                    : undefined
                }
              />
            ) : undefined
          }
        >
          {definitionPanels}
          {canShowStandaloneBrowserRail && <BrowserSessionsRail />}
        </AssetDefinitionLayout>
      ) : (
        // width="full": the page column sizes children to content, which pushed the rail off-screen.
        <Flex
          align="stretch"
          justify="center"
          gap={6}
          width="full"
          className="min-h-0 min-w-0 flex-1 bg-layout"
        >
          {definitionPanels}
          {/* The draft rail hosts sessions itself; other chat layouts get this column. */}
          {canShowStandaloneBrowserRail && <BrowserSessionsRail />}
        </Flex>
      )}

      {isChatPanelOpen && showEditor && (
        <ChatColumnDefaultWidth
          key={panelGroupAutoSaveId}
          panelRef={chatPanelRef}
          autoSaveId={panelGroupAutoSaveId}
          defaultSize={chatPanelSizes.defaultSize}
          isFrameMeasured={panelFrameWidth !== null && panelFrameWidth > 0}
        />
      )}

      {/* IDP Modal — same pattern as RunPage */}
      <Dialog
        open={isIDPPanelOpen}
        onOpenChange={open => {
          if (!open) closeIDPPanel();
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="flex h-[85vh] max-h-[85vh] w-[85vw] max-w-[85vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[85vw]"
        >
          <DialogTitle className="sr-only">
            {tOutputs('outputs.idp.modalTitle')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {tOutputs('outputs.idp.modalTitle')}
          </DialogDescription>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <Suspense fallback={<IDPPanelLoading />}>
              <IDPInlinePanel
                className="mb-0"
                onClose={closeIDPPanel}
                documentName={idpPanelDocumentName}
                fields={idpPanelFields}
                source={idpPanelSource}
                exceptionFields={idpPanelExceptionFields}
                idpData={idpPanelIdpData}
                onSave={idpPanelOnSave}
                readOnly={idpPanelReadOnly}
                idpListItems={idpPanelListItems}
              />
            </Suspense>
          </div>
        </DialogContent>
      </Dialog>

      <ExportAutomationDialog
        open={isExportDialogOpen}
        onClose={closeExportDialog}
        title={automation?.display_name ?? manifest?.title ?? ''}
        automationId={automationId}
        defaultRevisionId={automation?.version}
        isDraft={isDraft}
      />
    </>
  );

  const publishedHeaderProps = {
    automationId,
    title: automation?.display_name ?? '',
    // The draft's version beside the live one, so the version select can name both.
    automation: automation
      ? {
          version: liveDraftVersion ?? automation.version,
          latest_published_version:
            automation.latest_published_version || automation.version,
        }
      : undefined,
    hasDraft: hasLiveDraft,
    isStageLoading: !resolvedAutomationFromQuery || isLiveDraftLoading,
    versionView: 'live' as const,
    definitionHref: getRoutePath('AUTOMATION_AOP', {
      orgId: currentOrganizationId,
      workspaceId: currentWorkspaceId,
      automationId,
    }),
    view: 'definition' as const,
  };
  const publishedWorkspaceHeader = isVersionHistoryOpen ? (
    <AutomationWorkspaceHeader
      {...publishedHeaderProps}
      viewingVersion={selectedVersion ?? undefined}
      back={{
        label: tAutomation('automation.header.tooltip.exitVersionHistory'),
        onClick: handleCloseVersionHistory,
      }}
    />
  ) : isLiveAssetDocument ? (
    <AutomationWorkspaceHeader {...publishedHeaderProps} />
  ) : (
    renderAutomationPageHeader(({ titleActions, actions }) => (
      <AutomationWorkspaceHeader
        {...publishedHeaderProps}
        titleActions={titleActions}
        actions={actions}
      />
    ))
  );

  if (isPublishedDocument) {
    return (
      <AssetWorkspaceLayout
        ref={rootRef}
        kind="automations"
        header={publishedWorkspaceHeader}
        data-testid="automation-editor"
      >
        {editorContent}
      </AssetWorkspaceLayout>
    );
  }

  return isAssetWorkspace ? (
    <AssetWorkspaceLayout
      ref={rootRef}
      kind="automations"
      isDraft={documentVersion === 'draft'}
      header={
        <AutomationWorkspaceHeader
          automationId={automationId}
          title={automation?.display_name ?? ''}
          automation={automation ?? undefined}
          view="definition"
          versionView="draft"
          isStageLoading={!resolvedAutomationFromQuery}
          viewingVersion={
            isVersionHistoryOpen ? (selectedVersion ?? undefined) : undefined
          }
          back={
            isVersionHistoryOpen
              ? {
                  label: tAutomation(
                    'automation.header.tooltip.exitVersionHistory',
                  ),
                  onClick: handleCloseVersionHistory,
                }
              : undefined
          }
        />
      }
      data-testid="automation-editor"
    >
      {editorContent}
    </AssetWorkspaceLayout>
  ) : (
    <Flex
      ref={rootRef}
      direction="column"
      gap={isDraft ? 4 : 8}
      paddingX={pagePaddingUnits}
      className={cn('h-screen', isDraft ? 'pt-4' : 'pt-8')}
      width="full"
      data-testid="automation-editor"
    >
      {editorContent}
    </Flex>
  );
}
